import React, { useEffect, useState } from 'react';
import BreadCrumbs from '../../component/common/breadCrumbs';
import { useLocation, useNavigate, useSearchParams, useParams } from 'react-router-dom';
import { ToastTypes, useMessage } from '../../../base/context/MessageProvider';
import { useSubscriptionProgress } from '../../../base/context/SubscriptionProgressProvider';
import { ProductsApiService } from '../../../service/website/productsApiService';
import { SearchLogService } from '../../../service/admin/searchLogService';
import { productListApiService } from '../../../service/admin/productApiService';
import ProductCard from '../../component/common/productCard';
import { createMarkup, setUrl } from '../../helper/utils';
import { productPricingAlgorithm } from '../../../helper/utils';
import Pagination from '../../../component/library/pagination';
import Sorting from '../../component/views/collection/sorting';
import queryString from 'query-string';
import MobileFilteration from '../../component/views/collection/mobileFilteration';
import StickySelectionBar from '../../component/common/StickySelectionBar';
import SubscriptionModal from '../../component/common/SubscriptionModal';
import { getAuthLocalStorage } from '../../../storage/localStorage';
import AlibabaSubscriptionApiService from '../../../network/apiServices/AlibabaSubscriptionApiService';
import { ProductSubscriptionApiService } from '../../../service/seller/productSubscriptionApiService';
import SearchProductStorageService from '../../../network/apiServices/SearchProductStorageService';
import {
  getStoredSelectedProducts,
  storeSelectedProducts,
  clearStoredSelectedProducts,
  addProductToStorage,
  removeProductFromStorage,
  isProductStoredAsSelected,
} from '../../helper/selectedProductsStorage';
import logger from '../../helper/logger';
import SEO from '../../../component/common/SEO';
import { pageSEO, generateCategorySEO, generateSearchSEO } from '../../../config/seoConfig';
import SolrService from '../../../service/solr/solrService';
import { showLoginRestrictionPopup } from '../../component/common/LoginRestrictionPopup/loginPopup.js';
import LoginExploreCard from '../../component/common/LoginRestrictionPopup/loginExploreCard.js';
import ContactSupplierSection from '../../component/views/productDetails/ContactSupplierSection';
import SearchNavigator from '../../component/views/collection/searchNavigator';
import './style.css';
import { fetchToken } from '../../../lib/globalApiClient';

// Product display configuration from environment variables
const TOTAL_PRODUCTS = parseInt(process.env.REACT_APP_TOTAL_PRODUCTS || '4', 10);

export default function Products() {
  const message = useMessage();
  const { addSubscribingProduct, updateProductStatus, removeSubscribingProduct } = useSubscriptionProgress();
  const [products, setProducts] = useState([]);
  // Keep full result set for pagination, current page slice for display
  const [fullResultSet, setFullResultSet] = useState([]);
  // Stable fallbacks to avoid blank UI during/after loading
  const [stableProducts, setStableProducts] = useState([]);
  const [stableFullResultSet, setStableFullResultSet] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [hasSearched, setHasSearched] = useState(false);
  const [skeletonCount, setSkeletonCount] = useState(0);
  const [page, setPage] = useState(null);
  const location = useLocation();
  const latestReq = React.useRef(0);
  const abortControllerRef = React.useRef(null);
  const isSearching = React.useRef(false); // Track if search is in progress
  const lastProcessedSearch = React.useRef(''); // Track last processed search keyword
  const lastSavedKeywordLogRef = React.useRef(''); // Prevent duplicate save-keyword logs on pagination/re-renders
  const lastTagSearchRef = React.useRef(''); // Track last tag search URL to prevent duplicates
  const last400PopupReqIdRef = React.useRef(null); // Show restricted popup only once per request (instead of toast)

  const [queryParam, setQueryParam] = useState('');
  const [searchParams, setSearchParams] = useSearchParams();

  const [contentId, setContentId] = useState('');
  const [content, setContent] = useState();
  const [isAlibabaSearch, setIsAlibabaSearch] = useState(false);

  // Multi-product subscription state - grouped by keyword with persistence
  const [selectedProductsByKeyword, setSelectedProductsByKeyword] = useState({});
  const [showSubscriptionModal, setShowSubscriptionModal] = useState(false);
  const [subscriptionLoading, setSubscriptionLoading] = useState(false);
  const [currentKeyword, setCurrentKeyword] = useState('');
  const [currentCategory, setCurrentCategory] = useState('');

  const [pages, setPages] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
  });

  const navigate = useNavigate();
  const routeParams = useParams();
  const tagKeyword = routeParams.keyword; // Get keyword from /tag/:keyword route

  // Filter state - using main state only (no temp state)
  const [priceRange, setPriceRange] = useState([0, 10000]);
  const [ratingFilter, setRatingFilter] = useState(0);
  const [dropshippingFilter, setDropshippingFilter] = useState(false);
  const [productFoundInDB, setProductFoundInDB] = useState(false);

  // Amazon Friendly filter state
  const [showAmazonFriendly, setShowAmazonFriendly] = useState(false);

  // Subscribe Product filter state
  const [showSubscribedProducts, setShowSubscribedProducts] = useState(false);
  const [isCategoryLoading, setIsCategoryLoading] = useState(false); // Track category URL loading

  // Request for quote modal (from search list when Send Inquiry clicked for zero weight/dimension products)
  const [inquiryProduct, setInquiryProduct] = useState(null);
  const [allSubscribedProducts, setAllSubscribedProducts] = useState([]); // Store all subscribed products
  const [subscribedProductsPage, setSubscribedProductsPage] = useState(1); // Current page for subscribed products
  const SUBSCRIBED_PRODUCTS_PER_PAGE = 12; // Products per page for subscribed products

  // Dropship Product filter state
  const [showDropshipProducts, setShowDropshipProducts] = useState(false);

  // Backfill tracking - when all products are filtered out, fetch more from next page
  const isBackfillingRef = React.useRef(false);
  const backfillPageRef = React.useRef(1);
  const accumulatedProductsRef = React.useRef([]);

  // Track subscribed product offerIds/SKUs for filtering Alibaba products
  const [subscribedProductIds, setSubscribedProductIds] = useState(new Set());

  // Load persisted selected products on component mount
  useEffect(() => {
    const storedProducts = getStoredSelectedProducts();
    if (Object.keys(storedProducts).length > 0) {
      console.log('Loaded persisted selected products:', storedProducts);
      setSelectedProductsByKeyword(storedProducts);
    }

    // Load subscribed product IDs for filtering
    const loadSubscribedProductIds = async () => {
      if (isAuthenticatedSeller()) {
        try {
          // console.log('🔍 Loading subscribed products for filtering...');
          const response = await ProductSubscriptionApiService.getProductSubscription('?limit=10000');
          // console.log('📦 Subscribed products response:', response);

          if (response?.status?.code === 200 && response?.data?.products) {
            const ids = new Set();
            response.data.products.forEach(product => {
              if (product.sku) ids.add(String(product.sku));
              if (product.offerId) ids.add(String(product.offerId));
              if (product._id) ids.add(String(product._id));
            });
            setSubscribedProductIds(ids);
            // console.log('✅ Loaded subscribed product IDs:', ids.size);
            // console.log('📋 Subscribed IDs list:', Array.from(ids).slice(0, 10), '...(showing first 10)');
          } else {
            console.log('⚠️ No subscribed products found or unexpected response format');
          }
        } catch (error) {
          console.error('❌ Failed to load subscribed products for filtering:', error);
        }
      } else {
        console.log('⚠️ User not authenticated, skipping subscribed products load');
      }
    };

    loadSubscribedProductIds();
  }, []);

  // Persist selected products whenever they change
  useEffect(() => {
    if (Object.keys(selectedProductsByKeyword).length > 0) {
      storeSelectedProducts(selectedProductsByKeyword);
      console.log('Persisted selected products:', selectedProductsByKeyword);
    } else {
      // Clear storage when no products are selected
      clearStoredSelectedProducts();
    }
  }, [selectedProductsByKeyword]);

  // Check if user is authenticated seller
  const isAuthenticatedSeller = () => {
    const auth = getAuthLocalStorage();
    return auth.isLoggedIn && auth.loginHeader && (auth.userType === 'seller' || auth.userType === 'admin' || auth.userType === 'sub-admin');
  };

  // Check if user is logged in (any role)
  const isLoggedIn = () => {
    const auth = getAuthLocalStorage();
    return auth.isLoggedIn && auth.loginHeader;
  };

  // Extract search keyword and category from URL
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    let searchKeyword = params.get('search') || '';
    const categoryParam = params.get('category') || '';
    const pageParam = params.get('page');

    // if no explicit search query and url isn't under /collection, derive from path segments
    if (!searchKeyword && !location.pathname.startsWith('/collection')) {
      let pathSegments = location.pathname.split('/').filter(Boolean);
      // strip leading 'category' segment if present
      if (pathSegments[0] === 'category') {
        pathSegments = pathSegments.slice(1);
      }
      // in case static pages match, they will handle earlier via router; this logic still runs but segments
      // for categories should translate into space-separated keyword
      searchKeyword = pathSegments.join(' ');
      console.log("searchKeyword", searchKeyword)

      // Call combinedSearch function with the generated searchKeyword
      if (searchKeyword) {
        // Show loader immediately to prevent "No Products" section
        setIsLoading(true);
        setIsCategoryLoading(true);

        // also build a queryParam so later effects will use it when calling handleProducts
        setQueryParam(`?search=${encodeURIComponent(searchKeyword)}`);

        // Call combinedSearch immediately for category URLs
        (async () => {
          try {
            console.log('[Category URL] Calling combinedSearch with keyword:', searchKeyword);
            const categoryIdFromUrl = params.get('cid') || null;
            const combinedResult = await SearchProductStorageService.combinedSearch(searchKeyword, 1, TOTAL_PRODUCTS, false, categoryIdFromUrl);

            if (combinedResult.success && combinedResult.products?.length > 0) {
              sessionStorage.removeItem('search_bar_restricted_error');
              const processedProducts = await processProductsFromAPI(combinedResult.products);
              const sorted = filterSubscribedProducts(applySort(applyAlibabaFilters(processedProducts.filter(Boolean))));
              setProducts(sorted);
              setPages({
                currentPage: 1,
                totalPages: combinedResult.totalPages || 1,
                totalItems: combinedResult.totalItems || sorted.length,
              });
              console.log(`[Category URL] ✅ Loaded ${sorted.length} products for category: ${searchKeyword}`);
            } else {
              console.log(`[Category URL] ❌ No products found for category: ${searchKeyword}`);
              setProducts([]);
              setPages({ currentPage: 1, totalPages: 1, totalItems: 0 });
            }
          } catch (err) {
            console.error(`[Category URL] ❌ Error fetching products for category ${searchKeyword}:`, err);
            setProducts([]);
            setPages({ currentPage: 1, totalPages: 1, totalItems: 0 });
          } finally {
            // Hide loader regardless of success/failure
            setIsLoading(false);
            setIsCategoryLoading(false);
          }
        })();
      }
    }

    // Track search count per IP/user (only for new searches, not pagination)
    if (!isLoggedIn() && searchKeyword && !pageParam) {
      // Check if this is a new search (different from last processed)
      const currentSearchKey = `${searchKeyword}-${categoryParam || 'none'}`;

      // Only process if this is a new search (not already processed)
      if (currentSearchKey !== lastProcessedSearch.current) {
        lastProcessedSearch.current = currentSearchKey;

        // This is a new search (not pagination)
        const searchCount = parseInt(localStorage.getItem('userSearchCount') || '0', 10);

        if (searchCount >= 4) {
          // Show login popup using component
          showLoginRestrictionPopup('search', () => {
            // onLogin callback
            navigate('/login');
          }, () => {
            // onClose callback - navigate to home
            navigate('/');
          });

          // Don't proceed with search - popup will handle navigation
          // Don't set keyword/category to prevent triggering search
          return;
        }

        // Increment search count for new searches (only once per new search)
        localStorage.setItem('userSearchCount', String(searchCount + 1));
      }
    }

    // Only set keyword/category if search is allowed (not blocked by limit)
    setCurrentKeyword(searchKeyword);
    setCurrentCategory(categoryParam);
  }, [location.search, location.pathname, navigate]);

  // Handle product selection for multi-subscribe (grouped by keyword with persistence)
  const handleProductSelection = (product, isSelected) => {
    if (!isAuthenticatedSeller()) {
      navigate('/login');
      message.showToast('Please login as a seller to select products', ToastTypes.WARNING);
      return;
    }

    const keyword = currentKeyword || 'general';

    setSelectedProductsByKeyword(prev => {
      const newState = { ...prev };

      if (!newState[keyword]) {
        newState[keyword] = [];
      }

      if (isSelected) {
        // Add product if not already selected for this keyword
        const isAlreadySelected = newState[keyword].some(p => (p.sku || p.offerId) === (product.sku || product.offerId));
        if (!isAlreadySelected) {
          newState[keyword] = [...newState[keyword], { ...product, keyword }];
          // Also update localStorage immediately
          addProductToStorage(keyword, product);
        }
      } else {
        // Remove product from this keyword group
        const productId = product.sku || product.offerId;
        newState[keyword] = newState[keyword].filter(p => (p.sku || p.offerId) !== productId);

        // Remove from storage
        removeProductFromStorage(keyword, productId);

        // Clean up empty keyword groups
        if (newState[keyword].length === 0) {
          delete newState[keyword];
        }
      }

      return newState;
    });
  };

  // Handle subscription of multiple products with keyword and category metadata
  const handleMultipleSubscription = async subscriptionData => {
    if (!isAuthenticatedSeller()) {
      navigate('/login');
      message.showToast('Please login as a seller to subscribe', ToastTypes.WARNING);
      return;
    }

    setSubscriptionLoading(true);
    try {
      const { products } = subscriptionData;

      if (!products || products.length === 0) {
        message.showToast('No products to subscribe', ToastTypes.WARNING);
        return;
      }

      // Check if we're on a featured category page (URL contains 'fcId' or 'category=featured')
      const isFeaturedCategoryPage = location.pathname.includes('featured') ||
        location.search.includes('fcId=') ||
        location.search.includes('category=featured');

      // Check if we're on a global/Alibaba category page
      const isGlobalCategoryPage = location.search.includes('category=global');

      console.log('Subscription context:', {
        pathname: location.pathname,
        search: location.search,
        isFeaturedCategoryPage,
        isGlobalCategoryPage,
        totalProducts: products.length
      });

      // Separate MongoDB products (featured categories) from Alibaba products
      const mongoProducts = [];
      const alibabaProducts = [];

      products.forEach(product => {
        // Check if product is from MongoDB (featured categories)
        const originalData = product.alibaba_original_data || {};
        const productId = product.product_id;

        // Check if original data has MongoDB _id (most reliable indicator)
        const hasMongoId = originalData._id;

        // Check if product_id is MongoDB ObjectId format (24 hex characters)
        const isMongoObjectId = productId && productId.length === 24 && /^[0-9a-fA-F]{24}$/.test(productId);

        // Check location/source - MongoDB products don't have 'alibaba' or 'global' as location
        const location = String(originalData.location || '').toLowerCase();
        const source = String(originalData.source || '').toLowerCase();
        const isAlibabaLocation = location === 'alibaba' || location === 'global' || source === 'alibaba';

        // Check for status '5' which indicates MongoDB product (not Alibaba)
        const hasMongoStatus = originalData.status === '5';

        // Check if product_id is numeric and long (featured category products often have numeric SKUs)
        // But only if it doesn't have Alibaba location
        const isNumericSku = productId && /^\d+$/.test(productId) && productId.length >= 10;

        // Product is MongoDB if:
        // PRIORITY 1: If on category=global page, these are ALWAYS Alibaba products (from Alibaba API)
        // PRIORITY 2: If on featured category page, these are ALWAYS MongoDB products
        // PRIORITY 3: Check product data to determine type
        let isMongoProduct = false;

        if (isGlobalCategoryPage) {
          // On category=global page: ALWAYS Alibaba products (even if they have _id field in data)
          isMongoProduct = false;
          // console.log('Global category detection - forcing Alibaba:', {
          //   product_id: productId,
          //   hasMongoId,
          //   decidedMongo: false
          // });
        } else if (isFeaturedCategoryPage) {
          // On featured category page: default to MongoDB unless explicitly Alibaba with offerId
          isMongoProduct = !isAlibabaLocation || !originalData.offerId;
          // console.log('Featured category detection:', {
          //   product_id: productId,
          //   isAlibabaLocation,
          //   hasOfferId: !!originalData.offerId,
          //   decidedMongo: isMongoProduct
          // });
        } else {
          // Not on featured or global page: use detailed detection
          // PRIORITY CHECK: If product has MongoDB _id, it's a MongoDB product
          if (hasMongoId) {
            isMongoProduct = true;
            // console.log('MongoDB _id detected - treating as MongoDB product:', {
            //   product_id: productId,
            //   mongoId: hasMongoId,
            //   location,
            //   decidedMongo: true
            // });
          } else {
            // No MongoDB _id: check other indicators
            isMongoProduct = (
              (isMongoObjectId && !isAlibabaLocation) ||
              (hasMongoStatus && !isAlibabaLocation) ||
              (isNumericSku && !isAlibabaLocation && !location && !source)
            );
            // console.log('Non-featured detection (no MongoDB _id):', {
            //   product_id: productId,
            //   isMongoObjectId,
            //   hasMongoStatus,
            //   isNumericSku,
            //   isAlibabaLocation,
            //   decidedMongo: isMongoProduct
            // });
          }
        }

        if (isMongoProduct) {
          mongoProducts.push(product);
        } else {
          alibabaProducts.push(product);
        }
      });

      // console.log('Product separation:', { 
      //   total: products.length, 
      //   mongo: mongoProducts.length, 
      //   alibaba: alibabaProducts.length 
      // });

      // Handle MongoDB products separately using regular subscription API
      if (mongoProducts.length > 0) {
        const { SubscriptionApiService } = await import('../../../service/website/subscriptionApiService');
        const mongoSkus = mongoProducts.map(p => p.product_id).filter(Boolean);

        if (mongoSkus.length > 0) {
          try {
            // console.log('Subscribing MongoDB products:', mongoProducts.map(p => ({ 
            //   product_id: p.product_id, 
            //   keyword: p.keyword,
            //   sku: p.alibaba_original_data?.sku 
            // })));

            // Subscribe to MongoDB products one by one (or use batch if available)
            const mongoResults = await Promise.allSettled(
              mongoProducts.map(product =>
                SubscriptionApiService.subscribeProduct(product.product_id, product.keyword || '')
              )
            );

            // Log detailed results
            mongoResults.forEach((result, index) => {
              const product = mongoProducts[index];
              if (result.status === 'fulfilled') {
                console.log(`MongoDB subscription success for ${product.product_id}:`, result.value);
              } else {
                console.error(`MongoDB subscription failed for ${product.product_id}:`, result.reason);
              }
            });

            const mongoSuccess = mongoResults.filter(r => r.status === 'fulfilled' && r.value?.status?.code === 200).length;
            const mongoFailed = mongoResults.length - mongoSuccess;

            if (mongoSuccess > 0) {
              message.showToast(`${mongoSuccess} MongoDB product(s) subscribed successfully!`, ToastTypes.SUCCESS);
            }
            if (mongoFailed > 0) {
              console.warn(`${mongoFailed} MongoDB product(s) failed to subscribe`);
            }
          } catch (mongoError) {
            console.error('Error subscribing to MongoDB products:', mongoError);
          }
        }
      }

      // Only call Alibaba API if there are Alibaba products
      if (alibabaProducts.length === 0) {
        // All products were MongoDB, already handled above
        setSelectedProductsByKeyword({});
        clearStoredSelectedProducts();
        setShowSubscriptionModal(false);
        return;
      }

      // Log what we're sending to Alibaba API
      // console.log('Sending to Alibaba API:', {
      //   count: alibabaProducts.length,
      //   products: alibabaProducts.map(p => ({
      //     product_id: p.product_id,
      //     offerId: p.alibaba_original_data?.offerId,
      //     sku: p.alibaba_original_data?.sku,
      //     location: p.alibaba_original_data?.location,
      //     source: p.alibaba_original_data?.source,
      //     hasOriginalData: !!p.alibaba_original_data
      //   }))
      // });

      // Log full product structure to find offerId
      // console.log('Full product objects (first one):', {
      //   full_product: alibabaProducts[0],
      //   original_data_keys: Object.keys(alibabaProducts[0]?.alibaba_original_data || {}),
      //   top_level_keys: Object.keys(alibabaProducts[0] || {})
      // });

      // Ensure offerId is set - product_id and offerId are always the same
      // Prepare products with proper structure for backend
      const productsWithOfferId = alibabaProducts.map(product => {
        const originalData = product.alibaba_original_data || {};
        const offerId = product.product_id; // product_id IS the offerId

        // Build product structure matching backend expectations
        return {
          product_id: offerId, // Backend uses this as offerId
          keyword: product.keyword || currentKeyword || '',
          category_id: product.category_id || product.categoryId || null,
          generated_description: product.generated_description || product.description || null,
          sp: product.sp || product.dsFinalPrice || null,
          variations: product.variations || null,
          alibaba_original_data: {
            ...originalData,
            offerId: offerId, // Ensure offerId is set
            product_id: offerId // Also set product_id in original_data
          }
        };
      });

      console.log('Products prepared for Alibaba API:', {
        count: productsWithOfferId.length,
        sample: {
          product_id: productsWithOfferId[0]?.product_id,
          offerId_in_data: productsWithOfferId[0]?.alibaba_original_data?.offerId,
          keyword: productsWithOfferId[0]?.keyword
        }
      });

      // For single products, skip batch and go straight to one-by-one (more reliable)
      // For multiple products, try batch first, then fallback to one-by-one
      if (productsWithOfferId.length > 1) {
        // Try batch subscription for multiple products
        try {
          const result = await AlibabaSubscriptionApiService.subscribeToMultipleProductsWithMetadata(productsWithOfferId);

          if (result.status?.code === 200) {
            const successCount = result.data?.success?.length || 0;
            const failCount = result.data?.failed?.length || 0;
            const totalCount = result.data?.total || products.length;

            if (failCount === 0) {
              message.showToast(`${successCount} products subscribed successfully!`, ToastTypes.SUCCESS);
              setSelectedProductsByKeyword({});
              clearStoredSelectedProducts();
              setShowSubscriptionModal(false);
              return;
            } else if (successCount > 0) {
              message.showToast(`${successCount} of ${totalCount} products subscribed. ${failCount} failed.`, ToastTypes.WARNING);
              setSelectedProductsByKeyword({});
              clearStoredSelectedProducts();
              setShowSubscriptionModal(false);
              return;
            }
          }
        } catch (batchError) {
          // Silently fall through to one-by-one subscription
          // Don't log error here - only log if one-by-one also fails
        }
      }

      // Fallback: Subscribe one by one (works reliably, same as heart icon)
      // Use the same pattern as heart icon subscription (which works)
      const singleResults = await Promise.allSettled(
        productsWithOfferId.map(product => {
          // Use sku as offerId (same as heart icon does)
          const offerId = product.alibaba_original_data?.sku || product.product_id;
          // Get keyword from URL or product
          const urlParams = new URLSearchParams(window.location.search);
          const searchKeyword = urlParams.get('search') || product.keyword || currentKeyword || '';

          // Match heart icon pattern but with correct parameter order
          // Backend expects: (offerId, priceRanges, fromPDP, searchKeyword, sp, variations)
          // Pass empty arrays instead of undefined to avoid backend null errors
          return AlibabaSubscriptionApiService.subscribeToProduct(
            offerId,
            [], // priceRanges - empty array instead of undefined/null
            false, // fromPDP
            searchKeyword,
            undefined, // sp - can be undefined
            [] // variations - empty array instead of undefined/null
          );
        })
      );

      const singleSuccess = singleResults.filter(r => r.status === 'fulfilled' && r.value?.status?.code === 200).length;
      const singleFailed = singleResults.length - singleSuccess;

      if (singleSuccess > 0) {
        if (singleFailed === 0) {
          message.showToast(`${singleSuccess} product${singleSuccess > 1 ? 's' : ''} subscribed successfully!`, ToastTypes.SUCCESS);
        } else {
          message.showToast(`${singleSuccess} of ${productsWithOfferId.length} products subscribed. ${singleFailed} failed.`, ToastTypes.WARNING);
        }
        setSelectedProductsByKeyword({});
        clearStoredSelectedProducts();
        setShowSubscriptionModal(false);
      } else {
        // Only log errors if all subscriptions failed
        const errors = singleResults.filter(r => r.status === 'rejected').map(r => r.reason);
        console.error('All subscriptions failed:', errors);
        message.showToast('Failed to subscribe to products. Please try again.', ToastTypes.ERROR);
      }
    } catch (error) {
      console.error('Subscription error:', error);
      message.showToast(error?.message || 'Server error. Please try again later', ToastTypes.ERROR);
    } finally {
      setSubscriptionLoading(false);
    }
  };

  // Handle product removal from modal to sync with sticky bar
  const handleProductRemoveFromModal = (keyword, productId, updatedState) => {
    setSelectedProductsByKeyword(updatedState);
  };

  // Clear all selected products and cookies
  const handleClearSelections = () => {
    setSelectedProductsByKeyword({});
    clearStoredSelectedProducts();
    message.showToast('All selections cleared', ToastTypes.SUCCESS);
  };

  // Open subscription modal
  const handleOpenSubscriptionModal = () => {
    if (!isAuthenticatedSeller()) {
      navigate('/login');
      message.showToast('Please login as a seller to subscribe', ToastTypes.WARNING);
      return;
    }
    setShowSubscriptionModal(true);
  };

  // Check if a product is selected in current keyword context (with storage fallback)
  const isProductSelected = product => {
    const keyword = currentKeyword || 'general';
    const productId = product.sku || product.offerId;

    // Check in memory state first
    const keywordGroup = selectedProductsByKeyword[keyword];
    const inMemory = keywordGroup ? keywordGroup.some(p => (p.sku || p.offerId) === productId) : false;

    // Also check storage as fallback
    const inStorage = isProductStoredAsSelected(keyword, productId);

    return inMemory || inStorage;
  };

  // Get total selected products count across all keywords
  const getTotalSelectedCount = () => {
    return Object.values(selectedProductsByKeyword).reduce((total, products) => total + products.length, 0);
  };

  // Get selected products for current keyword
  const getCurrentKeywordSelectedProducts = () => {
    const keyword = currentKeyword || 'general';
    return selectedProductsByKeyword[keyword] || [];
  };

  // Handle Subscribe All functionality (direct subscribe with shared progress card)
  const handleSelectAll = () => {
    if (!isAuthenticatedSeller()) {
      navigate('/login');
      message.showToast('Please login as a seller to subscribe products', ToastTypes.WARNING);
      return;
    }

    const selectableProducts = (products && products.length ? products : stableProducts)?.filter(
      product => product?.moq === 1
    ) || [];

    if (selectableProducts.length === 0) {
      message.showToast('No subscribable products found on this page', ToastTypes.INFO);
      return;
    }

    // currently hidden becusese we dont use sticky baar for subscription 
    // const keyword = currentKeyword || 'general';

    // setSelectedProductsByKeyword(prev => {
    //   const newState = { ...prev };

    //   if (!newState[keyword]) {
    //     newState[keyword] = [];
    //   }

    //   // Add all selectable products that aren't already selected
    //   selectableProducts.forEach(product => {
    //     const isAlreadySelected = newState[keyword].some(p => (p.sku || p.offerId) === (product.sku || product.offerId));
    //     if (!isAlreadySelected) {
    //       newState[keyword] = [...newState[keyword], { ...product, keyword }];
    //       addProductToStorage(keyword, product);
    //     }
    //   });

    //   return newState;
    // });

    const urlParams = new URLSearchParams(window.location.search);
    const searchKeyword = urlParams.get('search') || currentKeyword || '';

    // Fire all subscriptions in background and show unified progress card
    selectableProducts.forEach(product => {
      const offerId = product?.sku || product?.offerId || product?.product_id || product?.alibaba_original_data?.sku;
      if (!offerId) return;

      addSubscribingProduct({ sku: offerId });

      AlibabaSubscriptionApiService.subscribeToProduct(
        offerId,
        [],
        false,
        searchKeyword,
        undefined,
        []
      )
        .then(res => {
          if (res?.status?.code === 200) {
            updateProductStatus(offerId, 'success', 'Subscribed');

            // Reflect immediately in listing cards
            setProducts(prev => prev.map(item => (
              (item?.sku || item?.offerId || item?.product_id) === offerId
                ? { ...item, subscribe: true }
                : item
            )));
            setStableProducts(prev => prev.map(item => (
              (item?.sku || item?.offerId || item?.product_id) === offerId
                ? { ...item, subscribe: true }
                : item
            )));

            setTimeout(() => removeSubscribingProduct(offerId), 2000);
          } else {
            const errorMsg = res?.error?.message || res?.status?.message || 'Failed to subscribe';
            updateProductStatus(offerId, 'error', errorMsg);
            setTimeout(() => removeSubscribingProduct(offerId), 3000);
          }
        })
        .catch(err => {
          const errorMsg = err?.message || err?.response?.data?.message || 'Server error. Try again later';
          updateProductStatus(offerId, 'error', errorMsg);
          setTimeout(() => removeSubscribingProduct(offerId), 3000);
        });
    });

    message.showToast(`Started subscribing ${selectableProducts.length} products`, ToastTypes.INFO);
  };

  // Handle Unselect All functionality
  const handleUnselectAll = () => {
    const keyword = currentKeyword || 'general';
    const currentSelections = selectedProductsByKeyword[keyword] || [];

    if (currentSelections.length === 0) {
      message.showToast('No products selected to unselect', ToastTypes.INFO);
      return;
    }

    setSelectedProductsByKeyword(prev => {
      const newState = { ...prev };

      // Remove all products for current keyword
      if (newState[keyword]) {
        // Clear from storage
        newState[keyword].forEach(product => {
          const productId = product.sku || product.offerId;
          removeProductFromStorage(keyword, productId);
        });

        delete newState[keyword];
      }

      return newState;
    });

    message.showToast(`Unselected ${currentSelections.length} products`, ToastTypes.SUCCESS);
  };

  // Get count of selectable products on current page
  const getSelectableProductsCount = () => {
    const currentProducts = (products && products.length ? products : stableProducts) || [];
    return currentProducts.filter(product => product?.moq === 1).length;
  };

  // Get count of selected products on current page
  const getSelectedProductsOnPageCount = () => {
    const currentProducts = (products && products.length ? products : stableProducts) || [];
    const selectableProducts = currentProducts.filter(product => product?.moq === 1);
    return selectableProducts.filter(product => isProductSelected(product)).length;
  };

  // Apply filters to products - FIXED RATING FILTER
  const applyFilters = (productsToFilter) => {
    if (!productsToFilter || !Array.isArray(productsToFilter)) return [];

    return productsToFilter.filter(product => {
      // Price filter
      const productPrice = Number(product?.sp || product?.consign_price || product?.priceInfo?.finalPrice || 0);
      if (productPrice < priceRange[0] || productPrice > priceRange[1]) {
        return false;
      }

      // Rating filter - COMPREHENSIVE extraction with fallbacks
      let productRating = 0;

      // Try multiple rating field names with proper fallbacks
      const ratingCandidates = [
        product?.supplierRating,
        product?.sellerRating,
        product?.storeRating,
        product?.rating,
        product?.avgStar,
        product?.starRating,
        product?.reviewRating,
        product?.evaluation?.starRating,
        product?.feedbackInfo?.positiveRate,
        product?.store?.rating,
        product?.supplier?.rating,
      ];

      // Find the first valid rating value
      for (const candidate of ratingCandidates) {
        if (candidate !== undefined && candidate !== null && candidate !== '') {
          const parsed = parseFloat(candidate);
          if (!isNaN(parsed) && parsed > 0) {
            productRating = parsed;
            break;
          }
        }
      }

      // If rating filter is set, check if product meets minimum rating - FIXED VERSION
      if (ratingFilter > 0) {
        // If product has no valid rating, exclude it when rating filter is applied
        if (productRating === 0 || isNaN(productRating)) {
          return false;
        }

        // Compare the actual rating with the filter threshold
        // No need for normalization - direct comparison works correctly
        if (productRating < ratingFilter) {
          return false;
        }
      }

      // Dropshipping filter
      if (dropshippingFilter) {
        // Strict 1-piece availability: show only when isOnePsale is true
        const moq = Number(product?.moq || 0);
        if (moq !== 1) return false;

        const tagInfoList =
          product?.tagInfoList ||
          product?.detailed_info?.tagInfoList ||
          product?.detailed_info?.productSaleInfo?.tagInfoList ||
          [];

        if (Array.isArray(tagInfoList) && tagInfoList.length > 0) {
          const isOnePsaleTag = tagInfoList.find(tag => tag?.key === 'isOnePsale');
          if (!isOnePsaleTag) return false;
          return isOnePsaleTag.value === true || isOnePsaleTag.value === 'true';
        }

        return product?.isOnePsale === true || product?.isOnePsale === 'true';
      }

      return true;
    });
  };

  // Handle applying filters - simplified with immediate application
  const handleApplyFilters = () => {
    // Apply filters to current products immediately
    const filteredProducts = applyFilters(fullResultSet);
    setProducts(filteredProducts);

    message.showToast('Filters applied successfully!', ToastTypes.SUCCESS);
  };

  // Handle resetting filters - simplified with immediate reset
  const handleResetFilters = () => {
    // Reset to default values
    setPriceRange([0, 10000]);
    setRatingFilter(0);
    setDropshippingFilter(false);

    // Reset to original products
    setProducts(fullResultSet);

    message.showToast('Filters reset successfully!', ToastTypes.SUCCESS);
  };

  // Handle advance filter click
  const handleAdvanceFilterClick = () => {
    message.showToast('Advanced filter functionality coming soon!', ToastTypes.INFO);
  };

  // Handle Amazon Friendly button - filter products with valid images
  const handleAmazonFriendlyClick = () => {
    if (showAmazonFriendly) {
      // Switch back to all products - restore from fullResultSet
      setShowAmazonFriendly(false);
      setProducts(fullResultSet.length > 0 ? fullResultSet : stableFullResultSet);
      message.showToast('Showing all products', ToastTypes.SUCCESS);
    } else {
      // Reset other filters
      setShowSubscribedProducts(false);
      setShowDropshipProducts(false);

      // Store current products before filtering
      const currentProducts = fullResultSet.length > 0 ? fullResultSet : products;
      if (fullResultSet.length === 0 && products.length > 0) {
        setFullResultSet(products);
      }

      // Filter products that have valid images
      const currentProductsList = currentProducts.length > 0 ? currentProducts : products;
      const amazonFriendlyProducts = currentProductsList.filter(product => {
        console.log('product', product)
        // Check if product has valid image and it's not empty
        // Handle cases where image might be null, undefined, or not a string
        // const whiteImageUrl = product?.whiteimage || product?.whiteImage || product?.isWhiteImageAvailable
        let whiteImageUrl;
        if (product?.whiteimage || product?.whiteImage) {
          whiteImageUrl = product.whiteimage || product.whiteImage;
        } else if (product?.isWhiteImageAvailable) {
          whiteImageUrl = product.image;
        }
        if (!whiteImageUrl) return false;
        // Convert to string if it's not already (handles numbers, etc.)
        const whiteImage = String(whiteImageUrl).trim();
        return whiteImage !== '';
      });

      setProducts(amazonFriendlyProducts);
      setShowAmazonFriendly(true);
      message.showToast(`Showing ${amazonFriendlyProducts.length} Amazon Friendly product(s)`, ToastTypes.SUCCESS);
    }
  };

  // Handle Subscribe Product button - show products matching search keyword
  const handleSubscribeProductClick = async () => {
    if (!isAuthenticatedSeller()) {
      navigate('/login');
      return;
    }

    if (showSubscribedProducts) {
      // Switch back to all products - restore from fullResultSet
      setShowSubscribedProducts(false);
      setProducts(fullResultSet.length > 0 ? fullResultSet : stableFullResultSet);
      setSubscribedProductsPage(1); // Reset page when switching back
      message.showToast('Showing all products', ToastTypes.SUCCESS);
    } else {
      // Reset other filters
      setShowAmazonFriendly(false);
      setShowDropshipProducts(false);

      // Get current search keyword from URL
      const params = new URLSearchParams(location.search);
      const searchKeyword = params.get('search') || '';

      if (!searchKeyword.trim()) {
        message.showToast('Please enter a search keyword first', ToastTypes.WARNING);
        return;
      }

      try {
        setIsLoading(true);

        // Store current products before filtering
        const currentProducts = fullResultSet.length > 0 ? fullResultSet : products;
        if (fullResultSet.length === 0 && products.length > 0) {
          setFullResultSet(products);
        }

        // Fetch subscribed products for this search keyword
        const response = await ProductSubscriptionApiService.getProductsBySearchKeyword(searchKeyword);

        if (response && response.status && response.status.code === 200 && response.data && response.data.products) {
          let subscribedProducts = response.data.products;

          if (subscribedProducts.length === 0) {
            message.showToast('No subscribed products found for this search keyword', ToastTypes.INFO);
            setIsLoading(false);
            return;
          }

          // Format products to ensure imgUrl is an array and set subscribe flag
          subscribedProducts = subscribedProducts.map(product => ({
            ...product,
            subscribe: true, // Mark as subscribed so heart icon shows as blue/filled
            imgUrl: Array.isArray(product.imgUrl) ? product.imgUrl : (product.imgUrl ? [product.imgUrl] : []),
            sp: Number(product.sp || 0),
            price: Number(product.price || 0),
            moq: Number(product.moq || 0),
          }));

          // Store all subscribed products
          setAllSubscribedProducts(subscribedProducts);
          setSubscribedProductsPage(1); // Reset to first page

          // Calculate pagination
          const totalPages = Math.ceil(subscribedProducts.length / SUBSCRIBED_PRODUCTS_PER_PAGE);

          // Show first page of products (12 products)
          const firstPageProducts = subscribedProducts.slice(0, SUBSCRIBED_PRODUCTS_PER_PAGE);
          setProducts(firstPageProducts);

          // Update pagination state for subscribed products
          setPages({
            currentPage: 1,
            totalPages: totalPages,
            totalItems: subscribedProducts.length,
          });

          setShowSubscribedProducts(true);
          message.showToast(`Showing ${subscribedProducts.length} subscribed product(s) for "${searchKeyword}"`, ToastTypes.SUCCESS);
        } else {
          message.showToast('Failed to fetch subscribed products', ToastTypes.ERROR);
        }
      } catch (error) {
        console.error('Error fetching subscribed products:', error);
        message.showToast('Error fetching subscribed products', ToastTypes.ERROR);
      } finally {
        setIsLoading(false);
      }
    }
  };

  // Handle pagination for subscribed products
  useEffect(() => {
    if (showSubscribedProducts && allSubscribedProducts.length > 0) {
      // Get current page from URL or state
      const params = new URLSearchParams(location.search);
      const urlPage = parseInt(params.get('page')) || 1;
      const currentPage = (page !== null && page !== undefined) ? page : urlPage;

      // Calculate pagination
      const totalPages = Math.ceil(allSubscribedProducts.length / SUBSCRIBED_PRODUCTS_PER_PAGE);
      const startIndex = (currentPage - 1) * SUBSCRIBED_PRODUCTS_PER_PAGE;
      const endIndex = startIndex + SUBSCRIBED_PRODUCTS_PER_PAGE;

      // Slice products for current page
      const pageProducts = allSubscribedProducts.slice(startIndex, endIndex);
      console.log('📄 Subscribed products pagination:', {
        currentPage,
        totalPages,
        startIndex,
        endIndex,
        showing: pageProducts.length,
        total: allSubscribedProducts.length
      });
      setProducts(pageProducts);

      // Update pagination state
      setPages({
        currentPage: currentPage,
        totalPages: totalPages,
        totalItems: allSubscribedProducts.length,
      });

      // Update subscribed products page state
      setSubscribedProductsPage(currentPage);
    }
  }, [page, showSubscribedProducts, allSubscribedProducts, location.search]);

  // Helper function to check if a product is dropshipping
  const isDropshippingProduct = (product) => {
    const moq = Number(product?.moq || 0);
    if (moq !== 1) return false;

    const tagInfoList =
      product?.tagInfoList ||
      product?.detailed_info?.tagInfoList ||
      product?.detailed_info?.productSaleInfo?.tagInfoList ||
      [];

    if (Array.isArray(tagInfoList) && tagInfoList.length > 0) {
      const isOnePsaleTag = tagInfoList.find(tag => tag?.key === 'isOnePsale');
      if (isOnePsaleTag) {
        return isOnePsaleTag.value === true || isOnePsaleTag.value === 'true';
      }
    }

    return product?.isOnePsale === true || product?.isOnePsale === 'true';
  };

  // Helper function to process products from API (reused from handleProducts)
  const processProductsFromAPI = async (products) => {
    // Phase 1: Process all products and collect calculation data WITHOUT making API calls
    const processedItems = await Promise.all(
      products.map(async (product, index) => {
        if (index === 0) {
          console.log('Processing product from API:', JSON.stringify(product))
        }
        const source = product._source || 'unknown';

        // Products from Solr or MongoDB already have pricing calculated
        if (source === 'solr' || source === 'mongodb') {
          return {
            mapped: {
              ...product,
              _id: product._id || product.objectID || product.sku,
              sku: String(product.sku || product.offerId || ''),
              title: product.title || product.subjectTrans || '',
              imgUrl: Array.isArray(product.imgUrl) ? product.imgUrl : [product.imgUrl].filter(Boolean),
              image: product.matchedVariantImage || product.image || product.imgUrl?.[0] || '',
              matchedVariantImage: product.matchedVariantImage || null,
              moq: Number(product.moq || 1),
              sp: Number(product.sp || product.finalPrice || 0),
              finalPrice: Number(product.finalPrice || product.sp || 0),
              location: product.location || 'global',
              handling_days: Number(product.handling_days || 15),
              month_sold: Number(product.month_sold || product.monthSoldOut || 0),
              rating: product.rating || product.avgStar || 4.0,
              variations: product.variations || [],
              mainVideo: product.mainVideo || null,
              isFromDB: source === 'mongodb',
              _source: source,
            },
            needsPricing: false,
          };
        }

        // Alibaba products — prepare data but skip API call
        if (source === 'alibaba') {
          const moqVal = Number(product.minOrderQuantity || product.moq || 0);
          const demoRating = (Math.random() * 1.5 + 3.5).toFixed(1);
          const variationsCount = product.detailed_info?.productSkuInfos?.length || 0;

          const collectAllImages = (item) => {
            const imgs = [];
            const detailedInfo = item.detailed_info || {};
            if (Array.isArray(detailedInfo.images) && detailedInfo.images.length > 0) {
              detailedInfo.images.forEach(img => {
                if (img && !imgs.includes(img)) imgs.push(img);
              });
            }
            if (item.imageUrl && !imgs.includes(item.imageUrl)) {
              imgs.push(item.imageUrl);
            }
            if (Array.isArray(item.imgUrl)) {
              item.imgUrl.forEach(img => {
                if (img && !imgs.includes(img)) imgs.push(img);
              });
            }
            return imgs.filter(Boolean);
          };
          const allProductImages = collectAllImages(product);
          const primaryImage = allProductImages[0] || product.imageUrl || '';

          let existingPrice = product.finalPrice || product.sp || product.priceInfo?.finalPrice || 0;
          let calculationPayload = product.calculationPayload || null;

          // If price already exists, no need to calculate
          if (existingPrice && existingPrice > 0) {
            const displayImage = product.matchedVariantImage || primaryImage;
            return {
              mapped: {
                ...product,
                _id: product.offerId || product.id || product.sku || Math.random().toString(36).slice(2),
                sku: String(product.offerId || product.id || product.sku || ''),
                title: product.subjectTrans || product.subject || product.title || '',
                sp: existingPrice,
                finalPrice: existingPrice,
                consign_price: existingPrice || product.priceInfo?.consignPrice || product.priceInfo?.price || 0,
                calculationPayload,
                imgUrl: allProductImages.length > 0 ? allProductImages : [primaryImage || product.imageUrl].filter(Boolean),
                image: displayImage || primaryImage,
                mainVideo: product.mainVideo || product.detailed_info?.mainVideo || null,
                moq: moqVal,
                isOnePsale: product.isOnePsale || moqVal === 1,
                handling_days: Number(product.leadTime || product.deliveryDays || 0) || 15,
                location: 'global',
                month_sold: Number(product.monthSold || 0),
                rating: product.rating || product.avgStar || demoRating,
                supplierRating: product.supplierRating || demoRating,
                sellerRating: product.sellerRating || demoRating,
                avgStar: product.avgStar || demoRating,
                variationsCount: variationsCount,
                variations: [],
                detailed_info: product.detailed_info,
                productImage: product.productImage,
                _source: source,
                matchedVariantImage: product.matchedVariantImage || null,
              },
              needsPricing: false,
            };
          }

          // Get calculation data without calling API (skipApiCall = true)
          try {
            const { product: preparedProduct, calculationData } = await productPricingAlgorithm(
              product, product.detailed_info?.productSaleInfo, product.detailed_info?.productShippingInfo, product.detailed_info?.productSkuInfos, true
            );
            const displayImage = product.matchedVariantImage || primaryImage;
            return {
              mapped: {
                ...product,
                _id: product.offerId || product.id || product.sku || Math.random().toString(36).slice(2),
                sku: String(product.offerId || product.id || product.sku || ''),
                title: product.subjectTrans || product.subject || product.title || '',
                sp: 0,
                finalPrice: 0,
                consign_price: product.priceInfo?.consignPrice || product.priceInfo?.price || 0,
                calculationPayload: calculationData,
                imgUrl: allProductImages.length > 0 ? allProductImages : [primaryImage || product.imageUrl].filter(Boolean),
                image: displayImage || primaryImage,
                mainVideo: product.mainVideo || product.detailed_info?.mainVideo || null,
                moq: moqVal,
                isOnePsale: product.isOnePsale || moqVal === 1,
                handling_days: Number(product.leadTime || product.deliveryDays || 0) || 15,
                location: 'global',
                month_sold: Number(product.monthSold || 0),
                rating: product.rating || product.avgStar || demoRating,
                supplierRating: product.supplierRating || demoRating,
                sellerRating: product.sellerRating || demoRating,
                avgStar: product.avgStar || demoRating,
                variationsCount: variationsCount,
                variations: [],
                detailed_info: product.detailed_info,
                productImage: product.productImage,
                _source: source,
                matchedVariantImage: product.matchedVariantImage || null,
              },
              needsPricing: true,
              calculationData,
            };
          } catch (err) {
            const fallbackDisplayImage = product.matchedVariantImage || primaryImage;
            return {
              mapped: {
                ...product,
                _id: product.offerId || product.id || product.sku || Math.random().toString(36).slice(2),
                sku: String(product.offerId || product.id || product.sku || ''),
                title: product.subjectTrans || product.subject || product.title || '',
                sp: 0, finalPrice: 0,
                imgUrl: allProductImages.length > 0 ? allProductImages : [primaryImage].filter(Boolean),
                image: fallbackDisplayImage || primaryImage,
                moq: moqVal, location: 'global', variations: [], _source: source,
                matchedVariantImage: product.matchedVariantImage || null,
                detailed_info: product.detailed_info,
              },
              needsPricing: false,
            };
          }
        }

        // Default case
        return {
          mapped: { ...product, _id: product._id || product.sku, sku: String(product.sku || ''), _source: source },
          needsPricing: false,
        };
      })
    );

    // Phase 2: Batch pricing — one API call for all products that need pricing
    const itemsNeedingPricing = processedItems.filter(item => item.needsPricing && item.calculationData);

    if (itemsNeedingPricing.length > 0) {
      try {
        const batchItems = itemsNeedingPricing.map(item => ({
          ...item.calculationData,
          topCategoryId: item.mapped?.topCategoryId,
          secondCategoryId: item.mapped?.secondCategoryId,
          thirdCategoryId: item.mapped?.thirdCategoryId,
          productShippingInfo: item.mapped?.detailed_info?.productShippingInfo,
        }));
        const batchResult = await ProductsApiService.calculatePricingAlgorithmBatch(batchItems);

        if (batchResult?.data?.results) {
          // Map results back by SKU
          const priceMap = {};
          const inquiryMap = {};
          batchResult.data.results.forEach(r => {
            if (r.success && r.breakdown?.FBP) {
              priceMap[r.sku] = r.breakdown.FBP;
            }
            // Capture requiresInquiry flag - true when weight/dims are 0 and no category matched
            if (r.requiresInquiry) {
              inquiryMap[r.sku] = true;
            }
          });

          // Also store breakdown data for access in ProductCard
          const breakdownMap = {};
          batchResult.data.results.forEach(r => {
            if (r.success && r.breakdown) {
              breakdownMap[r.sku] = r.breakdown;
            }
          });

          // Apply prices back to processed items
          processedItems.forEach(item => {
            if (item.needsPricing && item.calculationData?.sku) {
              const price = priceMap[item.calculationData.sku];
              if (price) {
                item.mapped.sp = price;
                item.mapped.finalPrice = price;
                item.mapped.consign_price = price || item.mapped.consign_price;
              }
              // Store breakdown data for ProductCard to use
              const breakdown = breakdownMap[item.calculationData.sku];
              if (breakdown) {
                item.mapped.breakdown = breakdown;
              }
              // Apply inquiry flag - product card will use this on /collection route
              item.mapped.requiresInquiry = !!inquiryMap[item.calculationData.sku];
            }
          });
        }
      } catch (batchErr) {
        console.error('[processProductsFromAPI] Batch pricing failed, falling back to individual calls:', batchErr);
        // Fallback: individual calls (old behavior)
        await Promise.all(
          itemsNeedingPricing.map(async (item) => {
            try {
              const result = await ProductsApiService.calculatePricingAlgorithm(item.calculationData);
              if (result?.data?.breakdown?.FBP) {
                item.mapped.sp = result.data.breakdown.FBP;
                item.mapped.finalPrice = result.data.breakdown.FBP;
                item.mapped.consign_price = result.data.breakdown.FBP || item.mapped.consign_price;
              }
            } catch (e) { /* ignore individual failures */ }
          })
        );
      }
    }

    return processedItems.map(item => item.mapped);
  };

  // Handle Dropship Product button - filter products with dropshipping available and fetch more if needed
  const handleDropshipProductClick = async () => {
    if (showDropshipProducts) {
      // Switch back to all products - restore from fullResultSet
      setShowDropshipProducts(false);
      setProducts(fullResultSet.length > 0 ? fullResultSet : stableFullResultSet);
      setSkeletonCount(0);

      // Clear dropship filter state from sessionStorage
      sessionStorage.removeItem('dropship_filter_active');
      sessionStorage.removeItem('dropship_filter_keyword');
      console.log('🗑️ Cleared dropship filter from sessionStorage');

      message.showToast('Showing all products', ToastTypes.SUCCESS);
    } else {
      // Reset other filters
      setShowAmazonFriendly(false);
      setShowSubscribedProducts(false);

      // Store current products before filtering
      const currentProducts = fullResultSet.length > 0 ? fullResultSet : products;
      if (fullResultSet.length === 0 && products.length > 0) {
        setFullResultSet(products);
      }

      try {
        setIsLoading(true);

        // Get search keyword from URL
        const params = new URLSearchParams(location.search);
        const searchKeyword = params.get('search') || '';
        const currentPage = parseInt(params.get('page')) || 1;

        // Filter current products for dropshipping
        const currentProductsList = currentProducts.length > 0 ? currentProducts : products;
        let dropshipProducts = currentProductsList.filter(isDropshippingProduct);

        const TARGET_COUNT = 12;

        // Show skeleton cards for the remaining products if we need to fetch more
        if (dropshipProducts.length > 0 && dropshipProducts.length < TARGET_COUNT) {
          const skeletonsNeeded = TARGET_COUNT - dropshipProducts.length;
          setSkeletonCount(skeletonsNeeded);
          console.log(`💀 Showing ${skeletonsNeeded} skeleton cards while fetching`);
        }

        // Only fetch more pages if we have a search keyword (for search results)
        if (searchKeyword && dropshipProducts.length < TARGET_COUNT) {
          const categoryIdFromUrl = params.get('cid') || null;
          let pageToFetch = currentPage;
          const maxPagesToFetch = 10; // Limit to prevent infinite loops

          // If we have less than 12 dropshipping products, fetch more pages
          while (dropshipProducts.length < TARGET_COUNT && pageToFetch - currentPage < maxPagesToFetch) {
            pageToFetch++;

            try {
              // Fetch next page
              const combinedResult = await SearchProductStorageService.combinedSearch(
                searchKeyword,
                pageToFetch,
                TOTAL_PRODUCTS,
                false,
                categoryIdFromUrl
              );

              if (!combinedResult.success || !combinedResult.products?.length) {
                // No more products available
                break;
              }

              // Process the fetched products
              const processedProducts = await processProductsFromAPI(combinedResult.products);
              const sorted = filterSubscribedProducts(applySort(applyAlibabaFilters(processedProducts.filter(Boolean))));

              // Filter for dropshipping products and add to our collection
              const newDropshipProducts = sorted.filter(isDropshippingProduct);
              dropshipProducts = [...dropshipProducts, ...newDropshipProducts];

              // If we've reached the target, break
              if (dropshipProducts.length >= TARGET_COUNT) {
                break;
              }
            } catch (fetchError) {
              console.error('Error fetching additional page:', fetchError);
              // Continue with what we have
              break;
            }
          }
        }

        // Limit to exactly 12 products
        dropshipProducts = dropshipProducts.slice(0, TARGET_COUNT);

        if (dropshipProducts.length === 0) {
          message.showToast('No dropshipping products found', ToastTypes.INFO);
          setSkeletonCount(0);
          setIsLoading(false);
          return;
        }

        setProducts(dropshipProducts);
        setShowDropshipProducts(true);
        setSkeletonCount(0);

        // Store dropship filter state in sessionStorage to persist across page navigation
        if (searchKeyword) {
          sessionStorage.setItem('dropship_filter_active', 'true');
          sessionStorage.setItem('dropship_filter_keyword', searchKeyword);
          console.log('💾 Saved dropship filter to sessionStorage:', { keyword: searchKeyword });
        }

        message.showToast(`Showing ${dropshipProducts.length} dropshipping product(s)`, ToastTypes.SUCCESS);
      } catch (error) {
        console.error('Error fetching dropshipping products:', error);
        message.showToast('Error fetching dropshipping products', ToastTypes.ERROR);
        setSkeletonCount(0);
      } finally {
        setIsLoading(false);
      }
    }
  };

  const handleProducts = async (query, opts = {}) => {
    // Prevent handleProducts from running when category URL is being processed
    if (isCategoryLoading) {
      console.log('🚫 Skipping handleProducts call during category URL loading');
      return;
    }

    // Handle tag-based product search (/tag/:keyword route)
    if (tagKeyword && location.pathname.startsWith('/tag/')) {
      const silent = !!opts.silent;
      const reqId = ++latestReq.current;

      // Cancel previous request
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
        await new Promise(resolve => setTimeout(resolve, 50));
      }
      abortControllerRef.current = new AbortController();

      // Prevent duplicate requests
      if (isSearching.current && !silent) {
        // console.log('⏸️ Tag search already in progress, skipping duplicate request');
        return;
      }

      isSearching.current = true;
      if (!silent) {
        setIsLoading(true);
      }

      try {
        const params = new URLSearchParams(location.search);
        const currentPage = parseInt(params.get('page')) || 1;
        const queryString = `page=${currentPage}`;

        const res = await ProductsApiService.getProductsByTag(tagKeyword, queryString);

        if (reqId !== latestReq.current) return;

        if (res.status.code === 200) {
          if (res.data) {
            const mapped = filterSubscribedProducts(applySort(res?.data?.products || []));

            // Check if dropship filter should be applied BEFORE setting products
            let productsToDisplay = mapped;
            const savedDropshipFilter = sessionStorage.getItem('dropship_filter_active');
            const savedDropshipKeyword = sessionStorage.getItem('dropship_filter_keyword');

            if (savedDropshipFilter === 'true' && savedDropshipKeyword === tagKeyword && mapped.length > 0) {
              const dropshipFiltered = mapped.filter(isDropshippingProduct);
              if (dropshipFiltered.length > 0) {
                productsToDisplay = dropshipFiltered;
                setShowDropshipProducts(true);
                console.log(`🔽 Auto-applied dropship filter for tag: ${dropshipFiltered.length} products`);
              } else {
                sessionStorage.removeItem('dropship_filter_active');
                sessionStorage.removeItem('dropship_filter_keyword');
                setShowDropshipProducts(false);
                setSkeletonCount(0);
              }
            }

            setProducts(productsToDisplay);
            if (mapped && mapped.length) setFullResultSet(mapped);
            setPages({
              currentPage: res.data.currentPage,
              totalPages: res.data.totalPages,
              totalItems: res.data.totalProduct,
            });
          }
        } else {
          setProducts([]);
          setFullResultSet([]);
          setPages({ currentPage: 1, totalPages: 0, totalItems: 0 });
          if (!silent) {
            message.showToast(res.error?.message || 'No products found', ToastTypes.INFO);
          }
        }
      } catch (err) {
        if (err.name === 'AbortError') {
          console.log('Tag search request cancelled');
          return;
        }
        if (reqId !== latestReq.current) return;
        message.showToast(err?.message || 'Server error. Try again later', ToastTypes.ERROR);
        setProducts([]);
        setFullResultSet([]);
        setPages({ currentPage: 1, totalPages: 0, totalItems: 0 });
      } finally {
        setIsLoading(false);
        setHasSearched(true);
        isSearching.current = false;
      }
      return; // Exit early for tag searches
    }

    // Check if user has reached search limit and is not logged in
    if (!isLoggedIn()) {
      const searchKeyword = new URLSearchParams(location.search).get('search') || '';
      const pageParam = new URLSearchParams(location.search).get('page');

      // Only check for new searches (not pagination)
      if (searchKeyword && !pageParam) {
        const searchCount = parseInt(localStorage.getItem('userSearchCount') || '0', 10);

        if (searchCount >= 4) {
          console.log('⏸ Search limit reached, blocking search request');
          setIsLoading(false);
          setHasSearched(true);
          return; // Don't proceed with search
        }
      }
    }

    const silent = !!opts.silent;
    const reqId = ++latestReq.current;

    // Cancel previous request and wait briefly for cleanup
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      await new Promise(resolve => setTimeout(resolve, 50));
    }
    abortControllerRef.current = new AbortController();

    // Check if this is a similar search pagination or image search pagination
    const params = new URLSearchParams(location.search);
    const categoryIdFromUrl = params.get('cid') || null;
    const isSimilarSearch = params.get('similarSearch') === 'true';
    const ali = params.get('ali');
    const isSimilarPagination = ali === 'upload' && isSimilarSearch;
    const urlPage = parseInt(params.get('page')) || 1;
    const effectivePage = (page !== null && page !== undefined) ? page : urlPage;
    const isImageSearchPagination = ali === 'ready' && effectivePage > 1;
    const allowPagination = isSimilarPagination || isImageSearchPagination;

    // For new keyword searches, always cancel previous and proceed
    const isNewKeywordSearch = params.get('search') && !allowPagination;

    // AGGRESSIVE DEDUPLICATION: Prevent multiple simultaneous searches
    // But allow pagination and new keyword searches
    if (isSearching.current && !silent && !allowPagination && !isNewKeywordSearch) {
      console.log('⏸️ Search already in progress, skipping duplicate request');
      return;
    }

    // Check global search lock - skip for pagination but allow new keyword searches
    if (!allowPagination && !isNewKeywordSearch) {
      const globalSearchLock = sessionStorage.getItem('global_search_lock');
      if (globalSearchLock && !silent) {
        const lockTimestamp = parseInt(globalSearchLock);
        const now = Date.now();
        if (now - lockTimestamp < 5000) {
          // Reduced to 5 seconds for better responsiveness
          console.log('⏸️ Global search lock active, skipping duplicate request');
          return;
        }
      }
    }

    // Set search state
    isSearching.current = true;
    if (!silent) {
      setIsLoading(true);
      // Set global search lock only for non-pagination searches
      if (!allowPagination) {
        sessionStorage.setItem('global_search_lock', Date.now().toString());
      }
    }

    // Clear previous search results for new keyword searches
    if (isNewKeywordSearch) {
      setProducts([]);
      setFullResultSet([]);
    }

    // Add request tracking
    const requestId = Date.now().toString();
    sessionStorage.setItem('current_request_id', requestId);

    try {
      logger.debug('handleProducts called with query', { query });
      if (query && query.includes('ali=keyword')) {
        setContentId('');
      }
      // params already defined above, use img
      const img = params.get('img');
      // Use effectivePage calculated earlier for consistency
      const currentPage = effectivePage;

      if (params.get('search') && params.get('category') && String(params.get('category')).toLowerCase() === 'global') {
        const searchKeyword = params.get('search');

        if (searchKeyword) {
          // Save keyword only for NEW searches (not pagination).
          const pageParam = parseInt(params.get('page') || '1', 10);
          const normalizedKeyword = String(searchKeyword).trim().toLowerCase();
          const saveKey = `${normalizedKeyword}|global|alibaba`;

          if (pageParam <= 1 && normalizedKeyword && lastSavedKeywordLogRef.current !== saveKey) {
            lastSavedKeywordLogRef.current = saveKey;
            SearchLogService.saveSearchKeyword(searchKeyword, 'global', 'alibaba').catch(err => {
              console.warn('Failed to save search keyword:', err);
            });
          }

          logger.info('Starting priority search', { searchKeyword, currentPage });
          console.log('[Priority Search] ========================================');
          console.log('[Priority Search] Starting search with priority: Solr → MongoDB → Alibaba API');
          console.log('[Priority Search] Keyword:', searchKeyword, '| Page:', currentPage);
          console.log('[Priority Search] ========================================');
          setIsAlibabaSearch(true);
          setIsLoading(true);
          try {
            // if product is from sku n exist in DB then show only that. if not then search from live ali products.
            // Only treat as SKU search if the ENTIRE keyword is numeric (e.g., "12345"), not if it just contains numbers (e.g., "iphone 12")
            if (/^[\d]+$/.test(searchKeyword.trim())) {
              const result = await ProductsApiService.getProductBySku(searchKeyword);

              if (result?.status?.code === 200 && result?.data) {
                const product = result.data;

                // Check if product is from Ali API (not in MongoDB)
                // Ali API products have location 'alibaba'/'global' and no valid MongoDB ObjectId _id
                const hasValidMongoId = product._id && /^[0-9a-fA-F]{24}$/.test(String(product._id));
                const isFromAliAPI = (product.location === 'alibaba' || product.location === 'global' || product.isFromAlibabaApi) && !hasValidMongoId;

                // Extract pricing - backend now calculates this for API products
                const finalPrice = Number(product.finalPrice || product.sp || product.priceInfo?.finalPrice || product.consign_price || 0);

                // Format the product to match expected structure (keep detailed_info for inquiry mode: Send Inquiry when zero weight/dimension)
                const formattedProduct = {
                  ...product,
                  // Only set _id if it's a valid MongoDB ObjectId, otherwise use sku for identification
                  _id: hasValidMongoId ? product._id : (product.sku || product.offerId),
                  sku: String(product.sku || ''),
                  title: product.title || '',
                  imgUrl: Array.isArray(product.imgUrl) ? product.imgUrl : [product.imgUrl].filter(Boolean),
                  moq: Number(product.moq || 0),
                  isOnePsale: product.isOnePsale || false,
                  handling_days: Number(product.handling_days || 15),
                  location: product.location || 'global',
                  month_sold: Number(product.month_sold || 0),
                  rating: product.rating || product.avgStar || 0,
                  supplierRating: product.supplierRating || product.rating || 0,
                  sellerRating: product.sellerRating || product.rating || 0,
                  avgStar: product.avgStar || product.rating || 0,
                  variations: product.variations || [],
                  detailed_info: product.detailed_info || {},
                  // Pricing fields - explicitly set for product card display
                  sp: finalPrice,
                  finalPrice: finalPrice,
                  consign_price: finalPrice,
                  mrp: Number(product.mrp || Math.ceil(finalPrice * 1.2)),
                  priceInfo: product.priceInfo || { finalPrice: finalPrice, price: finalPrice },
                  calculationPayload: product.calculationPayload || null,
                  priceRangeList: product.priceRangeList || [],
                };

                const mapped = filterSubscribedProducts(applySort(applyAlibabaFilters([formattedProduct])));
                console.log('📦 Single product after filtering:', mapped);
                setProducts(mapped);
                setStableProducts(mapped);
                setFullResultSet(mapped);

                setPages({
                  currentPage: 1,
                  totalPages: 1,
                  totalItems: 1,
                });

                setIsLoading(false);
                setHasSearched(true);
                isSearching.current = false;
                setIsAlibabaSearch(true);
                // Set productFoundInDB to false if it's from Ali API, true if it's from MongoDB
                setProductFoundInDB(!isFromAliAPI);
                return;
              }
            }

            // COMMENTED OUT: Solr search is disabled - now only using DB and API
            // Try Solr first - check for cached results for this specific page
            // const solrResult = await SolrService.searchProducts(searchKeyword, { 
            //   page: currentPage - 1, 
            //   hitsPerPage: TOTAL_PRODUCTS 
            // });
            // 
            // // Debug: Log Solr search results
            // console.log('[Solr Search Debug]', {
            //   keyword: searchKeyword,
            //   page: currentPage,
            //   hitsCount: solrResult.hits?.length || 0,
            //   nbHits: solrResult.nbHits,
            //   needsReindex: solrResult.needsReindex,
            //   hasHits: !!solrResult.hits,
            //   firstHitIndexedAt: solrResult.hits?.[0]?.indexedAt,
            //   firstHitSchemaVersion: solrResult.hits?.[0]?.schemaVersion,
            // });
            // 
            // // Check if we have valid cached results for this page
            // // Also check if cache needs re-indexing (schema update or expired)
            // // Use threshold of 1 product to consider cache valid (we show TOTAL_PRODUCTS per page)
            // const hasValidCache = solrResult.hits && solrResult.hits.length >= 1 && !solrResult.needsReindex;
            // const storedPagination = sessionStorage.getItem(`alibaba_pagination_${searchKeyword}`);
            // 
            // console.log('[Priority Search] Step 1: Solr Cache Check', {
            //   hasValidCache,
            //   hitsLength: solrResult.hits?.length,
            //   needsReindex: solrResult.needsReindex,
            //   threshold: 1,
            //   willUseSolr: hasValidCache
            // });
            // 
            // // Skip Solr cache - always use combinedSearch API for proper variant image matching
            // // The combinedSearch API enriches Solr products with MongoDB variations
            // // This ensures search keywords like "red bag" show the red variant image
            // if (hasValidCache) {
            //   console.log('[Solr] Solr cache available but skipping - using combinedSearch for variant image matching');
            //   // Fall through to combinedSearch below
            // }

            // Step 2 & 3: Use Combined Search API (fills up to TOTAL_PRODUCTS products from Solr + MongoDB + Alibaba)
            console.log('[Priority Search] Step 2: Using Combined Search API for keyword:', searchKeyword);
            try {
              const categoryIdFromUrl = params.get('cid') || null;
              const combinedResult = await SearchProductStorageService.combinedSearch(searchKeyword, currentPage, TOTAL_PRODUCTS, false, categoryIdFromUrl);

              if (combinedResult.success && combinedResult.products?.length > 0) {
                sessionStorage.removeItem('search_bar_restricted_error');
                console.log(`[Combined Search] Found ${combinedResult.products.length} products from [${combinedResult.sources?.join(' + ') || combinedResult.source}]`);

                // Process products using shared batch-pricing function
                const processedProducts = await processProductsFromAPI(combinedResult.products);

                const sorted = filterSubscribedProducts(applySort(applyAlibabaFilters(processedProducts.filter(Boolean))));

                // Set pagination from combined search result
                const paginationPages = combinedResult.totalPages || 1;
                const paginationItems = combinedResult.total || sorted.length;

                setPages({
                  currentPage: currentPage,
                  totalPages: paginationPages,
                  totalItems: paginationItems,
                });

                // Check if dropship filter should be applied BEFORE setting products
                let productsToDisplay = sorted;
                const savedDropshipFilter = sessionStorage.getItem('dropship_filter_active');
                const savedDropshipKeyword = sessionStorage.getItem('dropship_filter_keyword');

                console.log('🔍 Dropship filter check:', {
                  savedDropshipFilter,
                  savedDropshipKeyword,
                  currentSearchKeyword: searchKeyword,
                  sortedProductsCount: sorted.length,
                  page: currentPage
                });

                if (savedDropshipFilter === 'true' && savedDropshipKeyword === searchKeyword && sorted.length > 0) {
                  let dropshipFiltered = sorted.filter(isDropshippingProduct);
                  console.log(`✅ Filtering products: ${sorted.length} total → ${dropshipFiltered.length} dropship`);

                  const TARGET_COUNT = 12;

                  // If we have less than 12 dropship products, fetch more pages
                  if (dropshipFiltered.length > 0 && dropshipFiltered.length < TARGET_COUNT) {
                    console.log(`🔄 Need more products: ${dropshipFiltered.length}/${TARGET_COUNT}, fetching additional pages...`);

                    // Keep loader visible while fetching
                    setIsLoading(true);

                    // Show skeleton cards for the remaining products
                    const skeletonsNeeded = TARGET_COUNT - dropshipFiltered.length;
                    setSkeletonCount(skeletonsNeeded);
                    console.log(`💀 Showing ${skeletonsNeeded} skeleton cards`);

                    // Fetch additional pages in the background to reach 12 products
                    (async () => {
                      try {
                        let accumulatedDropship = [...dropshipFiltered];
                        let pageToFetch = currentPage;
                        const maxPagesToFetch = 10;

                        while (accumulatedDropship.length < TARGET_COUNT && pageToFetch - currentPage < maxPagesToFetch) {
                          pageToFetch++;

                          console.log(`📥 Fetching page ${pageToFetch} for more dropship products...`);

                          try {
                            const nextPageResult = await SearchProductStorageService.combinedSearch(
                              searchKeyword,
                              pageToFetch,
                              TOTAL_PRODUCTS,
                              false,
                              categoryIdFromUrl
                            );

                            if (!nextPageResult.success || !nextPageResult.products?.length) {
                              console.log(`⚠️ No more products available after page ${pageToFetch - 1}`);
                              break;
                            }

                            const processedNextPage = await processProductsFromAPI(nextPageResult.products);
                            const sortedNextPage = filterSubscribedProducts(applySort(applyAlibabaFilters(processedNextPage.filter(Boolean))));
                            const newDropship = sortedNextPage.filter(isDropshippingProduct);

                            console.log(`📦 Page ${pageToFetch} added ${newDropship.length} dropship products`);

                            accumulatedDropship = [...accumulatedDropship, ...newDropship];

                            if (accumulatedDropship.length >= TARGET_COUNT) {
                              console.log(`✅ Reached target: ${accumulatedDropship.length} products`);
                              break;
                            }
                          } catch (err) {
                            console.error(`❌ Error fetching page ${pageToFetch}:`, err);
                            break;
                          }
                        }

                        // Update with accumulated dropship products (limit to 12)
                        const finalDropship = accumulatedDropship.slice(0, TARGET_COUNT);
                        console.log(`🎯 Final dropship products: ${finalDropship.length}`);
                        setProducts(finalDropship);
                        setSkeletonCount(0);
                        setIsLoading(false);
                        isSearching.current = false;
                      } catch (err) {
                        console.error('❌ Error in background dropship fetch:', err);
                        setSkeletonCount(0);
                        setIsLoading(false);
                        isSearching.current = false;
                      }
                    })();
                  }

                  if (dropshipFiltered.length > 0) {
                    productsToDisplay = dropshipFiltered;
                    setShowDropshipProducts(true);
                    console.log(`🔽 Auto-applied dropship filter: ${dropshipFiltered.length} products on page ${currentPage}`);
                  } else {
                    // No dropshipping products found, clear the filter state
                    sessionStorage.removeItem('dropship_filter_active');
                    sessionStorage.removeItem('dropship_filter_keyword');
                    setShowDropshipProducts(false);
                    setSkeletonCount(0);
                  }
                }

                // Set products (either filtered or all)
                console.log(`📦 Setting products to display: ${productsToDisplay.length} products`);
                setProducts(productsToDisplay);
                setStableProducts(sorted); // Keep all products in stable state
                setFullResultSet(sorted); // Keep all products in fullResultSet

                // Set productFoundInDB based on sources
                const hasDBProducts = combinedResult.sources?.includes('mongodb') || combinedResult.source === 'mongodb';
                setProductFoundInDB(hasDBProducts);

                setIsLoading(false);
                setHasSearched(true);
                isSearching.current = false;

                // Store pagination for future use
                if (paginationPages > 1) {
                  const paginationData = { totalPages: paginationPages, totalItems: paginationItems, pageSize: TOTAL_PRODUCTS };
                  sessionStorage.setItem(`alibaba_pagination_${searchKeyword}`, JSON.stringify(paginationData));
                  // COMMENTED OUT: Solr pagination storage is disabled - now only using DB and API
                  // SolrService.storePaginationData(searchKeyword, paginationData).catch(() => {});
                }

                // Store Alibaba products to MongoDB in background (if any came from alibaba)
                // COMMENTED OUT: Solr indexing is disabled - now only using DB and API
                const alibabaProducts = sorted.filter(p => p._source === 'alibaba');
                if (alibabaProducts.length > 0) {
                  // COMMENTED OUT: Solr storage
                  // console.log(`[Combined Search] Storing ${alibabaProducts.length} Alibaba products to Solr in background`);
                  // Store to MongoDB in background (non-blocking)
                  (async () => {
                    try {
                      // COMMENTED OUT: Store to Solr
                      // await SolrService.indexProducts(alibabaProducts, searchKeyword, currentPage, combinedResult.totalPages || 1);
                      // console.log(`[Combined Search] ✅ Stored ${alibabaProducts.length} products to Solr`);

                      // Store to MongoDB with detailed info (this will fetch detailed info if missing)
                      console.log(`[Combined Search] 📦 Storing ${alibabaProducts.length} products to MongoDB in background`);
                      SearchProductStorageService.storeInBackground(
                        alibabaProducts,
                        searchKeyword,
                        currentPage,
                        combinedResult.totalPages || 1
                      );
                    } catch (err) {
                      console.error('[Combined Search] Error storing products:', err);
                    }
                  })();
                }

                console.log(`[Combined Search] Complete: ${sorted.length} products displayed from [${combinedResult.sources?.join(' + ') || combinedResult.source}]`);
                return;
              } else if (combinedResult.success && !(combinedResult.products?.length > 0)) {
                sessionStorage.removeItem('search_bar_restricted_error');
                // Combined search returned no products - show only API response (empty), do not fallback
                console.log('[Combined Search] No products returned - showing empty results (fallback disabled)');
                setProducts([]);
                setStableProducts([]);
                setFullResultSet([]);
                setPages({ currentPage: 1, totalPages: 1, totalItems: 0 });
                setSkeletonCount(0);
                setIsLoading(false);
                setHasSearched(true);
                isSearching.current = false;
                return;
              } else if (!combinedResult.success) {
                // Combined search failed (e.g. API returned 400) – use same UX as containsRestrictedSequence block (popup, no toast)
                setProducts([]);
                setStableProducts([]);
                setFullResultSet([]);
                setPages({ currentPage: 1, totalPages: 1, totalItems: 0 });
                setSkeletonCount(0);
                setIsLoading(false);
                setHasSearched(true);
                isSearching.current = false;
                if (combinedResult.statusCode === 400 && last400PopupReqIdRef.current !== reqId) {
                  last400PopupReqIdRef.current = reqId;
                  sessionStorage.setItem('search_bar_restricted_error', '⚠️ This search contains restricted content.');
                  navigate(-1);
                }
                return;
              }
            } catch (combinedError) {
              console.warn('[Combined Search] Failed - showing empty results (fallback disabled):', combinedError.message);
              setProducts([]);
              setStableProducts([]);
              setFullResultSet([]);
              setPages({ currentPage: 1, totalPages: 1, totalItems: 0 });
              setSkeletonCount(0);
              setIsLoading(false);
              setHasSearched(true);
              isSearching.current = false;
              if (combinedError.message?.includes?.('400') && last400PopupReqIdRef.current !== reqId) {
                last400PopupReqIdRef.current = reqId;
                sessionStorage.setItem('search_bar_restricted_error', '⚠️ This search contains restricted content.');
                navigate(-1);
              }
              return;
            }

            // Step 4: Final fallback - COMMENTED OUT: show only products from combined search API, do not call get-product-info
            /*
            // Step 4: Final fallback - Direct Alibaba API call (if combined search fails completely)
            console.log('[Priority Search] Step 4: Direct Alibaba API fallback for keyword:', searchKeyword);
            const payload = {
              keyword: searchKeyword,
              beginPage: currentPage,
              pageSize: TOTAL_PRODUCTS,
              country: 'en',
            };
            logger.debug('Making API request with payload', { payload });
            const response = await fetch('https://globalapi.importerr.com/get-product-info', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', 'X-API-Key': '9p1k6lin-fm9l-4jdc-ay8e-241apx2mcger' },
              body: JSON.stringify(payload),
              signal: abortControllerRef.current.signal,
            });
            if (reqId !== latestReq.current) return;
            const result = await response.json();
            logger.debug('API response received', { result });
            if (result?.status?.code === 500 || result?.error) {
              console.error('API returned error:', result);
              if (reqId !== latestReq.current) return;
              // Preserve existing products to avoid blank UI
              setPages(prev => ({ ...prev, currentPage: 1, totalPages: 1, totalItems: 0 }));
              setIsLoading(false);
              isSearching.current = false;
              message.showToast('API error: ' + (result?.error?.message || 'Unknown error'), ToastTypes.ERROR);
              return;
            }
            const data = result?.result?.result?.data || result?.result?.result?.list || result?.result?.result?.items || result?.data || result?.list || result?.items || [];
            // Calculate pricing before rendering
            const mappedWithPricing = await Promise.all(
              (Array.isArray(data) ? data : []).map(async (it, index) => {
                if (!it) return null;
                const moqVal = Number(it.minOrderQuantity || it.moq || 0);
                const demoRating = (Math.random() * 1.5 + 3.5).toFixed(1);
                const variationsCount = it.detailed_info?.productSkuInfos?.length || 0;
  
                // Collect ALL image URLs from various fields (Alibaba API structure)
                const collectAllImages = (item) => {
                  const imgs = [];
                  const detailedInfo = item.detailed_info || {};
  
                  // 1. Primary source: detailed_info.images array (contains all product images)
                  if (Array.isArray(detailedInfo.images) && detailedInfo.images.length > 0) {
                    detailedInfo.images.forEach(img => {
                      if (img && !imgs.includes(img)) imgs.push(img);
                    });
                  }
  
                  // 2. Add from imageUrl (thumbnail from search results)
                  if (item.imageUrl && !imgs.includes(item.imageUrl)) {
                    imgs.push(item.imageUrl);
                  }
  
                  // 3. Add from productImage if exists
                  if (item.productImage?.images) {
                    item.productImage.images.forEach(img => {
                      if (img && !imgs.includes(img)) imgs.push(img);
                    });
                  }
  
                  // 4. Add from images array directly
                  if (Array.isArray(item.images)) {
                    item.images.forEach(img => {
                      if (img && !imgs.includes(img)) imgs.push(img);
                    });
                  }
  
                  return imgs.filter(Boolean);
                };
                const allProductImages = collectAllImages(it);
                // For image field: use first image from array
                const primaryImageUrl = allProductImages[0] || it.imageUrl || '';
  
                try {
                  const { product, calculationData: calculationPayload } = await productPricingAlgorithm(
                    it, it.detailed_info?.productSaleInfo, it.detailed_info?.productShippingInfo, it.detailed_info?.productSkuInfos, true
                  );
                  const mappedProduct = {
                    ...product,
                    _id: it.offerId || it.id || Math.random().toString(36).slice(2),
                    sku: String(it.offerId || it.id || ''),
                    title: it.subjectTrans || it.subject || '',
                    sp: 0,
                    finalPrice: 0,
                    consign_price: 0,
                    calculationPayload,
                    imgUrl: allProductImages.length > 0 ? allProductImages : [primaryImageUrl || it.imageUrl].filter(Boolean),
                    image: primaryImageUrl,
                    mainVideo: it.mainVideo || it.detailed_info?.mainVideo || null,
                    moq: moqVal,
                    isOnePsale: it.isOnePsale || moqVal === 1,
                    handling_days: Number(it.leadTime || it.deliveryDays || 0) || 15,
                    location: 'global',
                    month_sold: Number(it.monthSold || 0),
                    rating: it.rating || it.avgStar || demoRating,
                    supplierRating: it.supplierRating || demoRating,
                    sellerRating: it.sellerRating || demoRating,
                    avgStar: it.avgStar || demoRating,
                    variationsCount: variationsCount,
                    variations: [],
                    detailed_info: it.detailed_info,
                    productImage: it.productImage,
                    _needsBatchPricing: true,
                  };
                  if (mappedProduct.mainVideo) {
                    console.log(`[Alibaba API] Product ${mappedProduct.sku} has mainVideo:`, mappedProduct.mainVideo);
                  }
                  return mappedProduct;
                } catch (error) {
                  const mappedProduct = {
                    ...it,
                    _id: it.offerId || it.id || Math.random().toString(36).slice(2),
                    sku: String(it.offerId || it.id || ''),
                    title: it.subjectTrans || it.subject || '',
                    sp: it.priceInfo?.finalPrice || 0,
                    consign_price: it.priceInfo?.finalPrice || 0,
                    imgUrl: allProductImages.length > 0 ? allProductImages : [primaryImageUrl || it.imageUrl].filter(Boolean),
                    image: primaryImageUrl,
                    mainVideo: it.mainVideo || it.detailed_info?.mainVideo || null,
                    moq: moqVal,
                    isOnePsale: it.isOnePsale || moqVal === 1,
                    handling_days: Number(it.leadTime || it.deliveryDays || 0) || 15,
                    location: 'global',
                    month_sold: Number(it.monthSold || 0),
                    rating: it.rating || it.avgStar || demoRating,
                    supplierRating: it.supplierRating || demoRating,
                    sellerRating: it.sellerRating || demoRating,
                    avgStar: it.avgStar || demoRating,
                    variationsCount: variationsCount,
                    variations: [],
                    detailed_info: it.detailed_info,
                    productImage: it.productImage,
                  };
                  return mappedProduct;
                }
              })
            );
  
            const rawMapped = mappedWithPricing.filter(Boolean);
  
            // Batch pricing: single API call for all products
            const batchItems = rawMapped.filter(p => p._needsBatchPricing && p.calculationPayload).map(p => p.calculationPayload);
            if (batchItems.length > 0) {
              try {
                const batchResult = await ProductsApiService.calculatePricingAlgorithmBatch(batchItems);
                if (batchResult?.data?.results) {
                  const priceMap = {};
                  const breakdownMap = {};
                  batchResult.data.results.forEach(r => { 
                    if (r.success && r.breakdown?.FBP) priceMap[r.sku] = r.breakdown.FBP;
                    if (r.success && r.breakdown) breakdownMap[r.sku] = r.breakdown;
                  });
                  rawMapped.forEach(p => {
                    if (p._needsBatchPricing && p.calculationPayload?.sku) {
                      const price = priceMap[p.calculationPayload.sku];
                      if (price) { p.sp = price; p.finalPrice = price; p.consign_price = price; }
                      // Store breakdown data for ProductCard to use
                      const breakdown = breakdownMap[p.calculationPayload?.sku];
                      if (breakdown) {
                        p.breakdown = breakdown;
                        p.breakdownData = breakdown;
                      }
                    }
                    delete p._needsBatchPricing;
                  });
                }
              } catch (batchErr) {
                console.error('[Alibaba keyword search] Batch pricing failed:', batchErr);
              }
            }
  
            console.log('🔍 Alibaba products before filtering:', rawMapped.length);
            console.log('📋 Sample Alibaba products:', rawMapped.slice(0, 3).map(p => ({
              title: p.title?.substring(0, 40),
              offerId: p.offerId,
              sku: p.sku,
              _id: p._id
            })));
            const mapped = filterSubscribedProducts(applySort(applyAlibabaFilters(rawMapped)));
            logger.info('Setting products', { count: mapped.length });
  
            // Preload first 3 images immediately for LCP
            if (mapped.length > 0) {
              mapped.slice(0, 3).forEach(product => {
                const imgUrl = product?.imgUrl?.[0];
                if (imgUrl && typeof imgUrl === 'string') {
                  const httpsUrl = imgUrl.replace(/^http:/, 'https:');
                  const link = document.createElement('link');
                  link.rel = 'preload';
                  link.as = 'image';
                  link.href = httpsUrl;
                  link.fetchPriority = 'high';
                  document.head.appendChild(link);
                }
              });
            }
  
            // Check if dropship filter should be applied BEFORE setting products
            let productsToDisplay = mapped;
            const savedDropshipFilter = sessionStorage.getItem('dropship_filter_active');
            const savedDropshipKeyword = sessionStorage.getItem('dropship_filter_keyword');
  
            console.log('🔍 Dropship filter check (Alibaba API):', {
              savedDropshipFilter,
              savedDropshipKeyword,
              currentSearchKeyword: searchKeyword,
              mappedProductsCount: mapped.length,
              page: currentPage
            });
  
            if (savedDropshipFilter === 'true' && savedDropshipKeyword === searchKeyword && mapped.length > 0) {
              let dropshipFiltered = mapped.filter(isDropshippingProduct);
              console.log(`✅ Filtering products: ${mapped.length} total → ${dropshipFiltered.length} dropship`);
  
              const TARGET_COUNT = 12;
  
              // If we have less than 12 dropship products, fetch more pages
              if (dropshipFiltered.length > 0 && dropshipFiltered.length < TARGET_COUNT) {
                console.log(`🔄 Need more products: ${dropshipFiltered.length}/${TARGET_COUNT}, fetching additional pages...`);
  
                // Keep loader visible while fetching
                setIsLoading(true);
  
                // Show skeleton cards for the remaining products
                const skeletonsNeeded = TARGET_COUNT - dropshipFiltered.length;
                setSkeletonCount(skeletonsNeeded);
                console.log(`💀 Showing ${skeletonsNeeded} skeleton cards`);
  
                // Fetch additional pages in the background to reach 12 products
                (async () => {
                  try {
                    let accumulatedDropship = [...dropshipFiltered];
                    let pageToFetch = currentPage;
                    const maxPagesToFetch = 10;
  
                    while (accumulatedDropship.length < TARGET_COUNT && pageToFetch - currentPage < maxPagesToFetch) {
                      pageToFetch++;
  
                      console.log(`📥 Fetching page ${pageToFetch} for more dropship products...`);
  
                      try {
                        const nextPageResult = await SearchProductStorageService.combinedSearch(
                          searchKeyword,
                          pageToFetch,
                          TOTAL_PRODUCTS,
                          false,
                          categoryIdFromUrl
                        );
  
                        if (!nextPageResult.success || !nextPageResult.products?.length) {
                          console.log(`⚠️ No more products available after page ${pageToFetch - 1}`);
                          break;
                        }
  
                        const processedNextPage = await processProductsFromAPI(nextPageResult.products);
                        const sortedNextPage = filterSubscribedProducts(applySort(applyAlibabaFilters(processedNextPage.filter(Boolean))));
                        const newDropship = sortedNextPage.filter(isDropshippingProduct);
  
                        console.log(`📦 Page ${pageToFetch} added ${newDropship.length} dropship products`);
  
                        accumulatedDropship = [...accumulatedDropship, ...newDropship];
  
                        if (accumulatedDropship.length >= TARGET_COUNT) {
                          console.log(`✅ Reached target: ${accumulatedDropship.length} products`);
                          break;
                        }
                      } catch (err) {
                        console.error(`❌ Error fetching page ${pageToFetch}:`, err);
                        break;
                      }
                    }
  
                    // Update with accumulated dropship products (limit to 12)
                    const finalDropship = accumulatedDropship.slice(0, TARGET_COUNT);
                    console.log(`🎯 Final dropship products: ${finalDropship.length}`);
                    setProducts(finalDropship);
                    setSkeletonCount(0);
                    setIsLoading(false);
                    isSearching.current = false;
                  } catch (err) {
                    console.error('❌ Error in background dropship fetch:', err);
                    setSkeletonCount(0);
                    setIsLoading(false);
                    isSearching.current = false;
                  }
                })();
              }
  
              if (dropshipFiltered.length > 0) {
                productsToDisplay = dropshipFiltered;
                setShowDropshipProducts(true);
                console.log(`🔽 Auto-applied dropship filter: ${dropshipFiltered.length} products on page ${currentPage}`);
              } else {
                // No dropshipping products found, clear the filter state
                sessionStorage.removeItem('dropship_filter_active');
                sessionStorage.removeItem('dropship_filter_keyword');
                setShowDropshipProducts(false);
                setSkeletonCount(0);
              }
            }
  
            // Set products (either filtered or all)
            console.log(`📦 Setting products to display: ${productsToDisplay.length} products`);
            setProducts(productsToDisplay);
            setStableProducts(mapped); // Keep all products in stable state
            setFullResultSet(mapped); // Keep all products in fullResultSet
  
            // Extract pagination from API response FIRST
            const responseData = result?.result?.result || result?.result || result;
  
            const totalRecords = responseData?.totalRecords || responseData?.totalItems || responseData?.total || 0;
            const apiTotalPages = responseData?.totalPage ||
              responseData?.totalPages ||
              responseData?.pages ||
              responseData?.total_page ||
              responseData?.pageCount ||
              1;
            const pageSize = responseData?.pageSize || TOTAL_PRODUCTS;
  
            const calculatedTotal = apiTotalPages * pageSize;
            const actualTotalItems = (totalRecords > 0 && totalRecords < pageSize) ? calculatedTotal : (totalRecords || calculatedTotal);
  
            // Store pagination info for this keyword
            const paginationData = {
              totalPages: apiTotalPages,
              totalItems: actualTotalItems,
              pageSize: pageSize,
              pagesIndexed: currentPage,
            };
  
            // Store in sessionStorage (same device)
            sessionStorage.setItem(`alibaba_pagination_${searchKeyword}`, JSON.stringify(paginationData));
  
            // COMMENTED OUT: Solr pagination and indexing is disabled - now only using DB and API
            // Store in Solr (cross-device for all users) - do this BEFORE indexing products
            // try {
            //   await SolrService.storePaginationData(searchKeyword, paginationData);
            // } catch (err) {
            //   // Silently handle pagination storage errors
            // }
  
            // Prepare variables for MongoDB storage (Solr indexing is disabled)
            // Use rawMapped (with all original API fields) for storage, not the filtered/sorted display data
            const productsToIndex = [...rawMapped]; // Clone to avoid mutation issues
            const keywordToIndex = searchKeyword;
            const pageToIndex = currentPage;
            const totalPagesToIndex = apiTotalPages;
  
            // COMMENTED OUT: Solr indexing
            // // NOW index products to Solr with correct totalPages
            // // LAZY LOADING: Only index the current page, not background pages
            // // Run indexing in background - don't block on it and don't cancel if request changes
            // // Fire and forget - index in background
            // (async () => {
            //   try {
            //     await SolrService.indexProducts(productsToIndex, keywordToIndex, pageToIndex, totalPagesToIndex);
            //     
            //     // Update pagination pagesIndexed count
            //     if (pageToIndex > 1) {
            //       const existingPagination = await SolrService.getPaginationData(keywordToIndex);
            //       if (existingPagination) {
            //         const newPagesIndexed = Math.max(existingPagination.pagesIndexed || 1, pageToIndex);
            //         await SolrService.storePaginationData(keywordToIndex, {
            //           ...existingPagination,
            //           pagesIndexed: newPagesIndexed,
            //         });
            //       }
            //     }
            //   } catch (err) {
            //     // Silently handle indexing errors
            //   }
            // })();
  
            // Store search results in MongoDB with S3 image upload (background)
            (async () => {
              try {
                SearchProductStorageService.storeInBackground(productsToIndex, keywordToIndex, pageToIndex, totalPagesToIndex);
              } catch (err) {
                // Silently handle storage errors
              }
            })();
            setPages({
              currentPage: currentPage,
              totalPages: paginationData.totalPages,
              totalItems: paginationData.totalItems,
            });
  
            // Products from Alibaba API are NOT in DB yet
            setProductFoundInDB(false);
  
            // Force pagination to show immediately
            setTimeout(() => {
              const paginationElement = document.querySelector('.collection_pagination');
              if (paginationElement) {
                paginationElement.style.display = 'block';
              }
            }, 0);
            setIsLoading(false);
            isSearching.current = false;
            return;
            */
          } catch (error) {
            console.error('Alibaba keyword search error:', error);
            if (reqId !== latestReq.current) return;
            // Preserve existing products to avoid blank UI
            setPages(prev => ({ ...prev, currentPage: 1, totalPages: 1, totalItems: 0 }));
            setIsLoading(false);
            setHasSearched(true);
            isSearching.current = false;
            // message.showToast('Failed to search Global products', ToastTypes.ERROR);
            return;
          }
        }
      }

      if (ali === 'ready') {
        // keep isAlibabaSearch derived from URL
        const cached = sessionStorage.getItem('alibaba_image_results');
        const cachedTimestamp = sessionStorage.getItem('alibaba_image_results_timestamp');
        const urlTimestamp = params.get('t');
        const paginationInfo = sessionStorage.getItem('alibaba_image_search_pagination');
        const base64 = sessionStorage.getItem('alibaba_image_search_base64');


        // For page 1: Check if we have cached results with matching timestamp (from Find Similar)
        // If yes, use cached results instead of calling API again
        if (currentPage === 1 && cached && cachedTimestamp && urlTimestamp && cachedTimestamp === urlTimestamp) {
          try {
            console.log('✅ Using cached results for page 1 (from Find Similar)');
            const cachedData = JSON.parse(cached);

            // Map products with pricing calculation (same as API response mapping)
            const mappedData = await Promise.all(
              cachedData.map(async it => {
                const moqVal = Number(it.minOrderQuantity || it.moq || 0);
                const isDropshipping = moqVal === 1;
                const { product, calculationData: calculationPayload } = await productPricingAlgorithm(it, it.detailed_info?.productSaleInfo, it.detailed_info?.productShippingInfo, it.detailed_info?.productSkuInfos, true);
                it = product;
                // Generate realistic ratings for demo
                const generateRealisticRating = () => {
                  const random = Math.random();
                  if (random < 0.1) return (Math.random() * 0.5 + 4.5).toFixed(1);
                  if (random < 0.3) return (Math.random() * 0.5 + 4.0).toFixed(1);
                  if (random < 0.6) return (Math.random() * 0.5 + 3.5).toFixed(1);
                  if (random < 0.85) return (Math.random() * 0.5 + 3.0).toFixed(1);
                  return (Math.random() * 0.5 + 2.5).toFixed(1);
                };

                const demoRating = parseFloat(generateRealisticRating());

                return {
                  ...it,
                  _id: it.offerId || it.id || Math.random().toString(36).slice(2),
                  sku: String(it.offerId || it.id || ''),
                  title: it.subjectTrans || it.subject || it.title || '',
                  sp: 0,
                  consign_price: 0,
                  calculationPayload,
                  imgUrl: [
                    String(
                      (it.imageUrl || it['product whiteimage'] || it['productWhiteimage'] || it.image || it.mainImage || it.productImage || '').replace(
                        /^http:/,
                        'https:'
                      )
                    ),
                  ].filter(Boolean),
                  moq: Number(it.minOrderQuantity || it.moq || 0),
                  isOnePsale: it.isOnePsale || isDropshipping,
                  handling_days: Number(it.detailed_info?.productShippingInfo?.leadTime || it.leadTime || it.deliveryDays || 0) || 15,
                  location: 'alibaba',
                  source: 'alibaba',
                  month_sold: Number(it.monthSold || it.month_sold || 0),
                  rating: it.rating || it.avgStar || it.starRating || it.reviewRating || it.supplierRating || demoRating,
                  supplierRating: it.supplierRating || it.rating || it.avgStar || demoRating,
                  sellerRating: it.sellerRating || it.storeRating || it.rating || demoRating,
                  avgStar: it.avgStar || it.starRating || it.rating || demoRating,
                  variations: [],
                };
              })
            );

            // Batch pricing: single API call for all cached image search products
            const batchItemsCached = mappedData.filter(p => p.calculationPayload).map(p => ({
              ...p.calculationPayload,
              topCategoryId: p.topCategoryId,
              secondCategoryId: p.secondCategoryId,
              thirdCategoryId: p.thirdCategoryId,
              productShippingInfo: p.detailed_info?.productShippingInfo,
            }));
            if (batchItemsCached.length > 0) {
              try {
                const batchResult = await ProductsApiService.calculatePricingAlgorithmBatch(batchItemsCached);
                if (batchResult?.data?.results) {
                  const priceMap = {};
                  const breakdownMap = {};
                  batchResult.data.results.forEach(r => { if (r.success && r.breakdown?.FBP) priceMap[r.sku] = r.breakdown.FBP; });
                  mappedData.forEach(p => {
                    if (p.calculationPayload?.sku) {
                      const price = priceMap[p.calculationPayload.sku];
                      if (price) { p.sp = price; p.finalPrice = price; p.consign_price = price; }
                      // Store breakdown data for ProductCard to use
                      const breakdown = breakdownMap[p.calculationPayload?.sku];
                      if (breakdown) {
                        p.breakdown = breakdown;
                        p.breakdownData = breakdown;
                      }
                    }
                  });
                }
              } catch (batchErr) {
                console.error('[Cached image search] Batch pricing failed:', batchErr);
              }
            }

            const mapped = filterSubscribedProducts(applySort(applyAlibabaFilters(mappedData)));
            const pageProducts = mapped.slice(0, TOTAL_PRODUCTS);

            // Update products state
            setProducts(pageProducts);
            if (pageProducts && pageProducts.length) {
              setStableProducts(pageProducts);
              setFullResultSet(pageProducts);
              setStableFullResultSet(pageProducts);
            }

            // Products from Alibaba image search are NOT in DB
            setProductFoundInDB(false);

            // Update pagination state from stored pagination info
            if (paginationInfo) {
              const pagination = JSON.parse(paginationInfo);
              setPages(prev => ({
                ...prev,
                currentPage: 1,
                totalPages: pagination.totalPage || 1,
                totalItems: pagination.totalRecords || pagination.totalItems || pageProducts.length,
              }));
            }

            const storedImageId = sessionStorage.getItem('alibaba_image_search_imageId');
            // Image search product persistence: handled by backend (get-product-info-by-image → enrichProductsInBackground).
            // Do not call /admin/search-products/store from frontend for img:* keywords (avoids duplicate / partial saves).
            // if (storedImageId && cachedData.length > 0) {
            //   const totalP = paginationInfo ? JSON.parse(paginationInfo).totalPage || 1 : 1;
            //   SearchProductStorageService.storeInBackground(cachedData, `img:${storedImageId}`, 1, totalP, storedImageId);
            // }

            // console.log('✅ Page 1 loaded from cache (Find Similar)');
            setIsLoading(false);
            setHasSearched(true);
            isSearching.current = false;
            return;
          } catch (error) {
            console.error('Error processing cached results:', error);
            // Fall through to API call if cached processing fails
          }
        }

        // Call API for page > 1, or if no cached results for page 1
        if (paginationInfo && base64) {
          try {
            const pagination = JSON.parse(paginationInfo);
            const totalPages = pagination.totalPage || 1;

            // Stop API call if it's beyond the last page
            if (currentPage > totalPages) {
              console.log('⏸️ Requested page exceeds total pages, stopping API call');
              setIsLoading(false);
              setHasSearched(true);
              isSearching.current = false;
              message.showToast('You have reached the last page', ToastTypes.INFO);
              return;
            }

            // Only call API if requested page is within valid range
            if (currentPage >= 1 && currentPage <= totalPages) {
              logger.info('Fetching page from new image search API', { currentPage, totalPages });
              setIsLoading(true);

              // Backend requires full data URI format (data:image/*); reconstruct if missing
              const base64ForApi = base64.startsWith('data:image/')
                ? base64
                : `data:image/jpeg;base64,${base64}`;
              const imageSearchUrl = process.env.REACT_APP_APIUrl
                ? `${process.env.REACT_APP_APIUrl}admin/get-product-info-by-image`
                : process.env.REACT_APP_APIUrl1688 + '/get-product-info-by-image';

              const token = !process.env.REACT_APP_APIUrl ? await fetchToken() : null;

              const imageSearchHeaders = process.env.REACT_APP_APIUrl
                ? { 'Content-Type': 'application/json' }
                : { 'Content-Type': 'application/json', 'X-API-Token': token };

              // Pass the original imageId so the backend reuses the same DB key for all pages
              const existingImageId = (() => {
                const sid = sessionStorage.getItem('alibaba_image_search_imageId');
                const st = sessionStorage.getItem('alibaba_image_timestamp');
                const ut = params.get('t');
                return (ut && st && ut === st && sid) ? sid : null;
              })();

              const response = await fetch(imageSearchUrl, {
                method: 'POST',
                headers: imageSearchHeaders,
                body: JSON.stringify({
                  imageBase64: base64ForApi,
                  beginPage: currentPage,
                  pageSize: TOTAL_PRODUCTS,
                  country: 'en',
                  ...(existingImageId ? { existingImageId } : {}),
                }),
              });

              if (reqId !== latestReq.current) return;

              const data = await response.json();

              if (data.status === 'success' && data.products) {
                // Update pagination info from API response
                if (data.pagination) {
                  sessionStorage.setItem('alibaba_image_search_pagination', JSON.stringify(data.pagination));
                }

                // Map products with pricing calculation
                const mappedData = await Promise.all(
                  data.products.map(async it => {
                    const moqVal = Number(it.minOrderQuantity || it.moq || 0);
                    const isDropshipping = moqVal === 1;
                    const { product, calculationData: calculationPayload } = await productPricingAlgorithm(it, it.detailed_info?.productSaleInfo, it.detailed_info?.productShippingInfo, it.detailed_info?.productSkuInfos, true);
                    it = product;
                    // Generate realistic ratings for demo
                    const generateRealisticRating = () => {
                      const random = Math.random();
                      if (random < 0.1) return (Math.random() * 0.5 + 4.5).toFixed(1);
                      if (random < 0.3) return (Math.random() * 0.5 + 4.0).toFixed(1);
                      if (random < 0.6) return (Math.random() * 0.5 + 3.5).toFixed(1);
                      if (random < 0.85) return (Math.random() * 0.5 + 3.0).toFixed(1);
                      return (Math.random() * 0.5 + 2.5).toFixed(1);
                    };

                    const demoRating = parseFloat(generateRealisticRating());

                    const tagInfoList = it.tagInfoList || it.detailed_info?.tagInfoList || it.detailed_info?.productSaleInfo?.tagInfoList || [];
                    return {
                      ...it,
                      _id: it.offerId || it.id || Math.random().toString(36).slice(2),
                      sku: String(it.offerId || it.id || ''),
                      title: it.subjectTrans || it.subject || it.title || '',
                      sp: 0,
                      consign_price: 0,
                      calculationPayload,
                      imgUrl: [
                        String(
                          (it.imageUrl || it['product whiteimage'] || it['productWhiteimage'] || it.image || it.mainImage || it.productImage || '').replace(
                            /^http:/,
                            'https:'
                          )
                        ),
                      ].filter(Boolean),
                      moq: Number(it.minOrderQuantity || it.moq || 0),
                      isOnePsale: it.isOnePsale || isDropshipping,
                      handling_days: Number(it.detailed_info?.productShippingInfo?.leadTime || it.leadTime || it.deliveryDays || 0) || 15,
                      location: 'alibaba',
                      source: 'alibaba',
                      month_sold: Number(it.monthSold || it.month_sold || 0),
                      tagInfoList,
                      rating: it.rating || it.avgStar || it.starRating || it.reviewRating || it.supplierRating || demoRating,
                      supplierRating: it.supplierRating || it.rating || it.avgStar || demoRating,
                      sellerRating: it.sellerRating || it.storeRating || it.rating || demoRating,
                      avgStar: it.avgStar || it.starRating || it.rating || demoRating,
                      variations: [],
                    };
                  })
                );

                // Batch pricing: single API call for image search products
                const batchItemsImg = mappedData.filter(p => p.calculationPayload).map(p => ({
                  ...p.calculationPayload,
                  topCategoryId: p.topCategoryId,
                  secondCategoryId: p.secondCategoryId,
                  thirdCategoryId: p.thirdCategoryId,
                  productShippingInfo: p.detailed_info?.productShippingInfo,
                }));
                if (batchItemsImg.length > 0) {
                  try {
                    const batchResult = await ProductsApiService.calculatePricingAlgorithmBatch(batchItemsImg);
                    if (batchResult?.data?.results) {
                      const priceMap = {};
                      const breakdownMap = {};
                      batchResult.data.results.forEach(r => {
                        if (r.success && r.breakdown?.FBP) priceMap[r.sku] = r.breakdown.FBP;
                        if (r.success && r.breakdown) breakdownMap[r.sku] = r.breakdown;
                      });
                      mappedData.forEach(p => {
                        if (p.calculationPayload?.sku) {
                          const price = priceMap[p.calculationPayload.sku];
                          if (price) { p.sp = price; p.finalPrice = price; p.consign_price = price; }
                          // Store breakdown data for ProductCard to use
                          const breakdown = breakdownMap[p.calculationPayload?.sku];
                          if (breakdown) {
                            p.breakdown = breakdown;
                            p.breakdownData = breakdown;
                          }
                        }
                      });
                    }
                  } catch (batchErr) {
                    console.error('[Image search API] Batch pricing failed:', batchErr);
                  }
                }

                const mapped = filterSubscribedProducts(applySort(applyAlibabaFilters(mappedData)));
                const pageProducts = mapped.slice(0, TOTAL_PRODUCTS);

                // Remove old cached results and replace with new data
                sessionStorage.removeItem('alibaba_image_results');
                sessionStorage.removeItem('alibaba_image_results_timestamp');

                // Store new results in sessionStorage for consistency
                const timestamp = Date.now().toString();
                sessionStorage.setItem('alibaba_image_results', JSON.stringify(pageProducts));
                sessionStorage.setItem('alibaba_image_results_timestamp', timestamp);

                // Update products state
                setProducts(pageProducts);
                if (pageProducts && pageProducts.length) {
                  setStableProducts(pageProducts);
                  setFullResultSet(pageProducts);
                  setStableFullResultSet(pageProducts);
                }

                // Products from Alibaba image search API are NOT in DB
                setProductFoundInDB(false);

                // Use existingImageId (sent to backend) so all pages share the same DB key.
                // Backend already used existingImageId for its own storage; mirror that here.
                const imageIdToUse = existingImageId || data.imageId;
                // Persist image-search hits via backend only (offerId → product-detail-by-offerid). No frontend store API.
                // if (imageIdToUse && data.products?.length) {
                //   SearchProductStorageService.storeInBackground(
                //     data.products.slice(0, TOTAL_PRODUCTS),
                //     `img:${imageIdToUse}`,
                //     currentPage,
                //     data.pagination?.totalPage || 1,
                //     imageIdToUse
                //   );
                // }

                // Update pagination state from API response
                const updatedPagination = data.pagination || pagination;
                setPages(prev => ({
                  ...prev,
                  currentPage: updatedPagination.currentPage || currentPage,
                  totalPages: Math.max(updatedPagination.totalPage || totalPages, 1),
                  totalItems: Math.max(updatedPagination.totalRecords || updatedPagination.totalItems || pageProducts.length, 0),
                }));

                setIsLoading(false);
                setHasSearched(true);
                isSearching.current = false;
                return;
              } else {
                console.error('API returned unsuccessful response:', data);
                message.showToast('Failed to load products. Please try again.', ToastTypes.ERROR);
                setIsLoading(false);
                setHasSearched(true);
                isSearching.current = false;
                return;
              }
            } else {
              console.log('⏸️ Page out of range, skipping API call');
              setIsLoading(false);
              setHasSearched(true);
              isSearching.current = false;
              return;
            }
          } catch (error) {
            console.error('Error fetching paginated results:', error);
            message.showToast('Failed to load page. Please try again.', ToastTypes.ERROR);
            setIsLoading(false);
            setHasSearched(true);
            isSearching.current = false;
            return;
          }
        } else {
          // No pagination info or base64 available
          console.log('⏸️ No pagination info or base64 available for ali=ready');
          setIsLoading(false);
          setHasSearched(true);
          isSearching.current = false;
          return;
        }
      }

      if (ali === 'upload') {
        // Check if this is a "Find Similar" search with pagination
        const isSimilarSearch = params.get('similarSearch') === 'true';
        const similarSearchImageUrl = sessionStorage.getItem('similarSearchImageUrl');
        if (isSimilarSearch && similarSearchImageUrl) {
          // This is a similar search - handle with proper pagination
          // console.log('🔍 Similar search pagination - Page:', currentPage);

          // Check if we have pre-loaded results for page 1
          const preloadedResults = sessionStorage.getItem('alibabaSearchResults');
          if (preloadedResults && currentPage === 1) {
            try {
              const resultsData = JSON.parse(preloadedResults);
              // console.log('📦 Loading pre-fetched similar products (Page 1):', resultsData);

              // Clear the stored results after reading (only for first page)

              // Map the products to the expected format
              const mappedData = await Promise.all(resultsData.products.map(async it => {
                const moqVal = Number(it.minOrderQuantity || it.moq || 0);
                const isDropshipping = moqVal === 1;
                const { product, calculationData: calculationPayload } = await productPricingAlgorithm(it, it.detailed_info?.productSaleInfo, it.detailed_info?.productShippingInfo, it.detailed_info?.productSkuInfos, true);
                it = product;

                // Generate realistic ratings for demo
                const generateRealisticRating = () => {
                  const random = Math.random();
                  if (random < 0.1) return (Math.random() * 0.5 + 4.5).toFixed(1);
                  if (random < 0.3) return (Math.random() * 0.5 + 4.0).toFixed(1);
                  if (random < 0.6) return (Math.random() * 0.5 + 3.5).toFixed(1);
                  if (random < 0.85) return (Math.random() * 0.5 + 3.0).toFixed(1);
                  return (Math.random() * 0.5 + 2.5).toFixed(1);
                };

                const demoRating = parseFloat(generateRealisticRating());

                return {
                  ...it,
                  _id: it.offerId || it.id || Math.random().toString(36).slice(2),
                  sku: String(it.offerId || it.id || ''),
                  title: it.subjectTrans || it.subject || it.title || '',
                  sp: 0,
                  consign_price: 0,
                  calculationPayload,
                  imgUrl: [
                    String(
                      (it['product whiteimage'] || it['productWhiteimage'] || it.imageUrl || it.image || it.mainImage || it.productImage || '').replace(
                        /^http:/,
                        'https:'
                      )
                    ),
                  ].filter(Boolean),
                  moq: Number(it.minOrderQuantity || it.moq || 0),
                  isOnePsale: it.isOnePsale || isDropshipping,
                  handling_days: Number(it.leadTime || it.deliveryDays || 0) || 15,
                  location: 'alibaba',
                  source: 'alibaba',
                  month_sold: Number(it.monthSold || it.month_sold || 0),
                  rating: it.rating || it.avgStar || it.starRating || it.reviewRating || it.supplierRating || demoRating,
                  supplierRating: it.supplierRating || it.rating || it.avgStar || demoRating,
                  sellerRating: it.sellerRating || it.storeRating || it.rating || demoRating,
                  avgStar: it.avgStar || it.starRating || it.rating || demoRating,
                  variations: [],
                };
              }));

              // Batch pricing: single API call for similar search preloaded products
              const batchItemsSimilar = mappedData.filter(p => p.calculationPayload).map(p => ({
                ...p.calculationPayload,
                topCategoryId: p.topCategoryId,
                secondCategoryId: p.secondCategoryId,
                thirdCategoryId: p.thirdCategoryId,
                productShippingInfo: p.detailed_info?.productShippingInfo,
              }));
              if (batchItemsSimilar.length > 0) {
                try {
                  const batchResult = await ProductsApiService.calculatePricingAlgorithmBatch(batchItemsSimilar);
                  if (batchResult?.data?.results) {
                    const priceMap = {};
                    const breakdownMap = {};
                    batchResult.data.results.forEach(r => {
                      if (r.success && r.breakdown?.FBP) priceMap[r.sku] = r.breakdown.FBP;
                      if (r.success && r.breakdown) breakdownMap[r.sku] = r.breakdown;
                    });
                    mappedData.forEach(p => {
                      if (p.calculationPayload?.sku) {
                        const price = priceMap[p.calculationPayload.sku];
                        if (price) { p.sp = price; p.finalPrice = price; p.consign_price = price; }
                        // Store breakdown data for ProductCard to use
                        const breakdown = breakdownMap[p.calculationPayload?.sku];
                        if (breakdown) {
                          p.breakdown = breakdown;
                          p.breakdownData = breakdown;
                        }
                      }
                    });
                  }
                } catch (batchErr) {
                  console.error('[Similar search preloaded] Batch pricing failed:', batchErr);
                }
              }

              const mapped = filterSubscribedProducts(applySort(applyAlibabaFilters(mappedData)));
              if (mapped && mapped.length) {
                setProducts(mapped);
                setFullResultSet(mapped);
                setStableProducts(mapped);
                setStableFullResultSet(mapped);
              }

              // Products from Alibaba image search are NOT in DB
              setProductFoundInDB(false);

              // Use API response data for pagination instead of hardcoded values
              const responseData = resultsData?.result?.result || resultsData?.result || resultsData;
              const apiTotalPages = responseData?.totalPage || responseData?.totalPages || 1;
              const apiTotalItems = responseData?.totalRecords || responseData?.totalItems || mapped.length;

              setPages(prev => ({
                ...prev,
                currentPage,
                totalPages: Math.max(apiTotalPages, 1),
                totalItems: Math.max(apiTotalItems, mapped.length),
              }));

              setIsLoading(false);
              setIsLoading(false);
              setHasSearched(true);
              isSearching.current = false;
              imageUploadRef.current = false; // Reset to allow pagination

              console.log('✅ Page 1 loaded from cache');
              return;
            } catch (error) {
              console.error('Error loading pre-fetched results:', error);
              // Continue with API call if parsing fails
            }
          }

          // For page 2+ or if no preloaded data, call the API with pagination
          // console.log('🌐 Calling API for similar search - Page:', currentPage);
          // console.log('🔗 Image URL:', similarSearchImageUrl);

          // Reset the imageUploadRef to allow API calls
          imageUploadRef.current = false;

          setIsLoading(true);
          isSearching.current = true;

          const apiPayload = {
            imageAddress: similarSearchImageUrl,
            beginPage: currentPage,
            pageSize: TOTAL_PRODUCTS,
            country: 'en',
          };

          // console.log('📤 API Request Payload:', apiPayload);

          try {
            const token = await fetchToken();
            const response = await fetch(`${process.env.REACT_APP_APIUrl1688}/product-by-image`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', 'X-API-Token': token },
              body: JSON.stringify(apiPayload),
            });

            if (reqId !== latestReq.current) {
              setIsLoading(false);
              setIsLoading(false);
              setHasSearched(true);
              isSearching.current = false;
              return;
            }

            if (!response.ok) {
              throw new Error(`API request failed: ${response.status}`);
            }

            const data = await response.json();
            // console.log('✅ Similar products API response (Page ' + currentPage + '):', data);

            const productsList = data?.result?.result?.data || data?.result?.data || data?.data || [];
            // console.log('📦 Products received:', productsList.length);

            // Map the products to the expected format
            const mappedData = await Promise.all(productsList.map(async it => {
              const moqVal = Number(it.minOrderQuantity || it.moq || 0);
              const isDropshipping = moqVal === 1;

              const { product, calculationData: calculationPayload } = await productPricingAlgorithm(it, it.detailed_info?.productSaleInfo, it.detailed_info?.productShippingInfo, it.detailed_info?.productSkuInfos, true);
              it = product;

              // Generate realistic ratings for demo
              const generateRealisticRating = () => {
                const random = Math.random();
                if (random < 0.1) return (Math.random() * 0.5 + 4.5).toFixed(1);
                if (random < 0.3) return (Math.random() * 0.5 + 4.0).toFixed(1);
                if (random < 0.6) return (Math.random() * 0.5 + 3.5).toFixed(1);
                if (random < 0.85) return (Math.random() * 0.5 + 3.0).toFixed(1);
                return (Math.random() * 0.5 + 2.5).toFixed(1);
              };

              const demoRating = parseFloat(generateRealisticRating());

              return {
                ...it,
                _id: it.offerId || it.id || Math.random().toString(36).slice(2),
                sku: String(it.offerId || it.id || ''),
                title: it.subjectTrans || it.subject || it.title || '',
                sp: 0,
                consign_price: 0,
                calculationPayload,
                imgUrl: [
                  String(
                    (it['product whiteimage'] || it['productWhiteimage'] || it.imageUrl || it.image || it.mainImage || it.productImage || '').replace(
                      /^http:/,
                      'https:'
                    )
                  ),
                ].filter(Boolean),
                moq: Number(it.minOrderQuantity || it.moq || 0),
                isOnePsale: it.isOnePsale || isDropshipping,
                handling_days: Number(it.leadTime || it.deliveryDays || 0) || 15,
                location: 'alibaba',
                source: 'alibaba',
                month_sold: Number(it.monthSold || it.month_sold || 0),
                rating: it.rating || it.avgStar || it.starRating || it.reviewRating || it.supplierRating || demoRating,
                supplierRating: it.supplierRating || it.rating || it.avgStar || demoRating,
                sellerRating: it.sellerRating || it.storeRating || it.rating || demoRating,
                avgStar: it.avgStar || it.starRating || it.rating || demoRating,
                variations: [],
              };
            }));

            // Batch pricing: single API call for similar search API products
            const batchItemsSimilarApi = mappedData.filter(p => p.calculationPayload).map(p => ({
              ...p.calculationPayload,
              topCategoryId: p.topCategoryId,
              secondCategoryId: p.secondCategoryId,
              thirdCategoryId: p.thirdCategoryId,
              productShippingInfo: p.detailed_info?.productShippingInfo,
            }));
            if (batchItemsSimilarApi.length > 0) {
              try {
                const batchResult = await ProductsApiService.calculatePricingAlgorithmBatch(batchItemsSimilarApi);
                if (batchResult?.data?.results) {
                  const priceMap = {};
                  batchResult.data.results.forEach(r => { if (r.success && r.breakdown?.FBP) priceMap[r.sku] = r.breakdown.FBP; });
                  mappedData.forEach(p => {
                    if (p.calculationPayload?.sku) {
                      const price = priceMap[p.calculationPayload.sku];
                      if (price) { p.sp = price; p.finalPrice = price; p.consign_price = price; }
                    }
                  });
                }
              } catch (batchErr) {
                console.error('[Similar search API] Batch pricing failed:', batchErr);
              }
            }

            const mapped = filterSubscribedProducts(applySort(applyAlibabaFilters(mappedData)));
            // console.log('📊 Products after filters/sort:', mapped.length);

            if (mapped && mapped.length) {
              setProducts(mapped);
              setFullResultSet(mapped);
              setStableProducts(mapped);
              setStableFullResultSet(mapped);
            }

            // Products from Alibaba image search API are NOT in DB
            setProductFoundInDB(false);

            // Use API response data for pagination
            const responseData = data?.result?.result || data?.result || data;
            const apiTotalPages = responseData?.totalPage || responseData?.totalPages || 1;
            const apiTotalItems = responseData?.totalRecords || responseData?.totalItems || mapped.length;

            setPages(prev => ({
              ...prev,
              currentPage,
              totalPages: Math.max(apiTotalPages, 1),
              totalItems: Math.max(apiTotalItems, mapped.length),
            }));

            setIsLoading(false);
            setIsLoading(false);
            setHasSearched(true);
            isSearching.current = false;

            // console.log('✅ Page ' + currentPage + ' loaded successfully');
            return;
          } catch (error) {
            console.error('❌ Error fetching similar products for page', currentPage, ':', error);
            setIsLoading(false);
            setIsLoading(false);
            setHasSearched(true);
            isSearching.current = false;
            message.showToast('Failed to load products', ToastTypes.ERROR);
            return;
          }
        }

        // Prevent multiple image upload calls
        if (imageUploadRef.current && !silent) {
          console.log('Image upload already in progress, skipping duplicate request');
          return;
        }

        // Check global upload lock
        const globalUploadLock = sessionStorage.getItem('global_image_upload_lock');
        if (globalUploadLock && !silent) {
          console.log('Global image upload in progress, skipping duplicate request');
          return;
        }

        imageUploadRef.current = true;
        if (!silent) {
          setIsLoading(true);
          // Set global upload lock with timestamp
          sessionStorage.setItem('global_image_upload_lock', Date.now().toString());
        }

        let b64 = sessionStorage.getItem('alibaba_image_upload_b64') || '';
        if (!b64) {
          // Wait briefly for base64 to appear (first time)
          b64 = (await waitForSessionValue('alibaba_image_upload_b64', 10, 120)) || '';
        }
        const storedTimestamp = sessionStorage.getItem('alibaba_image_timestamp');
        const urlTimestamp = params.get('t');
        if (!b64) {
          // give up gracefully on first page to avoid blank state
          setIsLoading(false);
          setHasSearched(true);
          isSearching.current = false;
          imageUploadRef.current = false;
          sessionStorage.removeItem('global_image_upload_lock');
          message.showToast('Image not ready. Please try again.', ToastTypes.WARNING);
          return;
        } else if (storedTimestamp && urlTimestamp && storedTimestamp !== urlTimestamp) {
          logger.debug('Timestamp mismatch, ignoring old upload');
          setIsLoading(false);
          setHasSearched(true);
          isSearching.current = false;
          imageUploadRef.current = false;
          sessionStorage.removeItem('global_image_upload_lock');
          return;
        } else {
          // Removed redundant productListApiService call, going directly to AWS API
          const safeParse = async resp => {
            try {
              return await resp.json();
            } catch {
              return {};
            }
          };
          const extractImageId = (obj, depth = 0) => {
            if (!obj || depth > 5) return '';
            if (typeof obj === 'string') {
              try {
                const j = JSON.parse(obj);
                return extractImageId(j, depth + 1);
              } catch { }
              const m = obj.match(/imageId"?\s*[:=]\s*"?(\d{6,})/i);
              return m ? m[1] : '';
            }
            if (typeof obj !== 'object') return '';
            if (obj.imageId) return String(obj.imageId);
            for (const k of Object.keys(obj)) {
              if (/imageid|image_id/i.test(k) && obj[k]) return String(obj[k]);
              const v = extractImageId(obj[k], depth + 1);
              if (v) return v;
            }
            return '';
          };
          try {
            const controllerU1 = new AbortController();
            const tU1 = setTimeout(() => controllerU1.abort(), 8000);
            const token1 = await fetchToken();
            const uploadResp = await fetch(`${process.env.REACT_APP_APIUrl1688}/product-image-upload`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', 'X-API-Token': token1 },
              body: JSON.stringify({ imageBase64: b64 }),
              signal: controllerU1.signal,
            });
            clearTimeout(tU1);
            const uploadJson = await safeParse(uploadResp);
            let imageId = uploadJson?.result?.result || extractImageId(uploadJson);
            if (!imageId) {
              // Try with stripped base64 (remove data URL prefix if present)
              const stripped = String(b64).replace(/^data:[^,]+,/, '');
              const controllerU2 = new AbortController();
              const tU2 = setTimeout(() => controllerU2.abort(), 8000);
              const token2 = await fetchToken();
              const uploadResp2 = await fetch(`${process.env.REACT_APP_APIUrl1688}/product-image-upload`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'X-API-Token': token2 },
                body: JSON.stringify({ imageBase64: stripped }),
                signal: controllerU2.signal,
              });
              clearTimeout(tU2);
              const uploadJson2 = await safeParse(uploadResp2);
              imageId = uploadJson2?.result?.result || extractImageId(uploadJson2);
            }
            if (!imageId) {
              if (reqId !== latestReq.current) return;
              console.log('No image ID found, but preserving any existing results');
              // keep isAlibabaSearch derived from URL
              setIsLoading(false);
              setHasSearched(true);
              isSearching.current = false;
              imageUploadRef.current = false;
              return;
            }
            const controllerID = new AbortController();
            const tID = setTimeout(() => controllerID.abort(), 8000);
            const token3 = await fetchToken();
            const idResp = await fetch(`${process.env.REACT_APP_APIUrl1688.replace(/\/$/, '')}/product-image-id`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', 'X-API-Token': token3 },
              body: JSON.stringify({ imageId }),
              signal: controllerID.signal,
            });
            clearTimeout(tID);
            const idJson = await safeParse(idResp);
            if (reqId !== latestReq.current) return;
            const result = idJson?.result?.result || idJson?.result || idJson || {};
            const data = result?.data || result?.list || result?.items || [];

            // Generate realistic ratings for demo
            const generateRealisticRating = () => {
              const random = Math.random();
              if (random < 0.1) return (Math.random() * 0.5 + 4.5).toFixed(1);
              if (random < 0.3) return (Math.random() * 0.5 + 4.0).toFixed(1);
              if (random < 0.6) return (Math.random() * 0.5 + 3.5).toFixed(1);
              if (random < 0.85) return (Math.random() * 0.5 + 3.0).toFixed(1);
              return (Math.random() * 0.5 + 2.5).toFixed(1);
            };

            const mappedData = (Array.isArray(data) ? data : []).map(it => {
              const demoRating = parseFloat(generateRealisticRating());
              return {
                ...it,
                _id: it.offerId || it.id || Math.random().toString(36).slice(2),
                sku: String(it.offerId || it.id || ''),
                title: it.subjectTrans || it.subject || '',
                sp: Number(it.priceInfo?.price || 0),
                imgUrl: [
                  String((it['product whiteimage'] || it['productWhiteimage'] || it.imageUrl || it.image || it.mainImage || it.productImage || '').replace(/^http:/, 'https:')),
                ].filter(Boolean),
                moq: Number(it.minOrderQuantity || 0),
                isOnePsale: it.isOnePsale || false,
                handling_days: Number(it.leadTime || it.deliveryDays || 0) || 15,
                location: 'alibaba',
                month_sold: Number(it.monthSold || 0),
                // COMPREHENSIVE RATING DATA
                rating: it.rating || it.avgStar || it.starRating || it.reviewRating || it.supplierRating || demoRating,
                supplierRating: it.supplierRating || it.rating || it.avgStar || demoRating,
                sellerRating: it.sellerRating || it.storeRating || it.rating || demoRating,
                avgStar: it.avgStar || it.starRating || it.rating || demoRating,
                variations: [],
              };
            });
            const filtered = applyAlibabaFilters(mappedData);
            const allMapped = filterSubscribedProducts(applySort(filtered));
            sessionStorage.setItem('alibaba_image_results', JSON.stringify(allMapped));
            sessionStorage.setItem('alibaba_image_results_timestamp', params.get('t') || Date.now().toString());
            sessionStorage.setItem('alibaba_image_search_imageId', String(imageId));
            // Image search DB save: backend background flow only; do not POST search-products/store from here.
            // if (imageId && (Array.isArray(data) ? data : []).length > 0) {
            //   SearchProductStorageService.storeInBackground(Array.isArray(data) ? data : [], `img:${imageId}`, 1, 1, imageId);
            // }
            const itemsPerPage = TOTAL_PRODUCTS;
            const startIndex = (currentPage - 1) * itemsPerPage;
            const endIndex = startIndex + itemsPerPage;
            const mapped = allMapped.slice(startIndex, endIndex);
            setProducts(mapped);
            // Store full result set for pagination
            if (allMapped && allMapped.length) {
              setFullResultSet(allMapped);
              setStableFullResultSet(allMapped);
              setStableProducts(mapped);
            }
            const totalItems = allMapped.length;
            const totalPages = Math.ceil(totalItems / itemsPerPage);
            setPages(prev => ({
              ...prev,
              currentPage: currentPage,
              totalPages: totalPages,
              totalItems: totalItems,
            }));
            // keep isAlibabaSearch derived from URL
            setIsLoading(false);
            setHasSearched(true);
            isSearching.current = false;
            imageUploadRef.current = false;
            sessionStorage.removeItem('global_image_upload_lock');
            return;
          } catch (e) {
            if (reqId !== latestReq.current) return;
            console.log('Alternative upload method failed, but preserving any existing results:', e);
            // keep isAlibabaSearch derived from URL
            setIsLoading(false);
            setHasSearched(true);
            isSearching.current = false;
            imageUploadRef.current = false;
            sessionStorage.removeItem('global_image_upload_lock');
            return;
          }
        }
      }
      // If we are in any Alibaba mode but for some reason did not return above,
      // avoid falling through to the default DB-backed product list. We only want
      // to show Global (Alibaba) API results for these flows.
      if (ali || (params.get('category') && String(params.get('category')).toLowerCase() === 'global')) {
        // Save search keyword for Alibaba searches (if search keyword exists)
        // This handles cases where ali param exists but category is not 'global' or missing
        const searchKeyword = params.get('search');
        if (searchKeyword) {
          SearchLogService.saveSearchKeyword(searchKeyword, 'global', 'alibaba').catch(err => {
            console.warn('Failed to save search keyword:', err);
          });
        }
        setPages({ currentPage: 1, totalPages: 1, totalItems: 0 });
        setIsLoading(false);
        setHasSearched(true);
        isSearching.current = false;
        return;
      }
      // Save search keyword for DB searches (non-Alibaba searches)
      const searchParam = params.get('search');
      const categoryParam = params.get('category') || '';
      if (searchParam) {
        // Save keyword for all DB searches (with or without category)
        // Use 'global' as category if no category specified (means "All Categories")
        const finalCategory = categoryParam || 'all';
        SearchLogService.saveSearchKeyword(searchParam, finalCategory, 'website').catch(err => {
          console.warn('Failed to save search keyword:', err);
        });
      }

      const res = await ProductsApiService.getProducts(query);
      if (reqId !== latestReq.current) return;
      if (res.status.code === 200) {
        if (res.data) {
          const mapped = filterSubscribedProducts(applySort(res?.data?.products || []));
          setProducts(mapped);
          if (mapped && mapped.length) setFullResultSet(mapped);
          setPages(prev => ({
            ...prev,
            currentPage: res.data.currentPage,
            totalPages: res.data.totalPages,
            totalItems: res.data.totalProducts,
          }));
        } else {
          // Preserve existing products to avoid blank UI
        }
        // Only redirect when success and no error - do not redirect to /collection on any error
        if (res.status.code === 200 && !res.error && res.data?.searchOutsideCategory) {
          navigate(`/collection?search=${searchParams.get('search')}`);
        }
      } else {
        // Preserve existing products to avoid blank UI
        message.showToast(res.error?.message || 'Failed to fetch products', ToastTypes.ERROR);
      }
      setIsLoading(false);
      setHasSearched(true);
    } catch (err) {
      if (err.name === 'AbortError') {
        console.log('Request cancelled');
        return;
      }
      if (reqId !== latestReq.current) return;
      message.showToast(err?.message || 'Server error. Try again later', ToastTypes.ERROR);
      setIsLoading(false);
      setHasSearched(true);
    } finally {
      // Reset search flag
      isSearching.current = false;
      // Clean up global locks
      sessionStorage.removeItem('global_search_lock');
      sessionStorage.removeItem('global_image_upload_lock');
      sessionStorage.removeItem('global_api_lock');
      sessionStorage.removeItem('current_request_id');
    }
  };

  const applyAlibabaFilters = items => {
    try {
      const params = new URLSearchParams(location.search);
      const minInr = Number(params.get('min') || 0);
      const maxInr = Number(params.get('max') || 200000000); // large cap
      const soldMin = Number(params.get('monthSoldMin') || 0);
      const soldMax = Number(params.get('monthSoldMax') || 999999999);
      const shippingCountry = (params.get('shippingCountry') || '').split(',').filter(Boolean);
      const wantChina = shippingCountry.includes('china');
      const wantIndia = shippingCountry.includes('india');

      const filtered = (Array.isArray(items) ? items : []).filter(p => {
        // Skip price filtering for Alibaba products since we use algorithm-calculated prices
        const isAlibaba = String(p?.location || '').toLowerCase() === 'alibaba' || String(p?.location || '').toLowerCase() === 'global' || p?.source === 'alibaba';

        if (isAlibaba) {
          // For Alibaba products, skip price filtering as we use algorithm-calculated prices
          // Price filtering will be handled by the algorithm calculation in ProductCard
          return true;
        }

        // For regular products, use simple price filtering
        const basePrice = Number(p?.sp || p?.price || 0);
        if (basePrice < minInr || basePrice > maxInr) return false;

        const ms = Number(p?.month_sold || p?.monthSold || 0);
        if (ms < soldMin || ms > soldMax) return false;

        // shipping country filter (alibaba/global -> china)
        if (shippingCountry.length) {
          const loc = String(p?.location || '').toLowerCase();
          const isChinaItem = loc === 'alibaba' || loc === 'global' || loc === 'china';
          const isIndiaItem = loc === 'india';
          let ok = false;
          if (wantChina && isChinaItem) ok = true;
          if (wantIndia && isIndiaItem) ok = true;
          if (!ok) return false;
        }
        return true;
      });
      return filtered;
    } catch (e) {
      return Array.isArray(items) ? items : [];
    }
  };

  // Filter out subscribed products - users should only see unsubscribed products
  // Note: This filters both DATABASE products and ALIBABA products
  // BUT skips filtering for direct SKU/offerId searches
  const filterSubscribedProducts = React.useCallback((items) => {
    try {
      if (!Array.isArray(items)) return [];

      // Check if this is a direct SKU/offerId search (only digits)
      const params = new URLSearchParams(location.search);
      const searchKeyword = params.get('search') || '';
      const isSkuSearch = /^[\d]+$/.test(searchKeyword.trim());

      if (isSkuSearch) {
        console.log('🔍 Direct SKU/offerId search detected, skipping subscription filter');
        return items; // Don't filter for direct SKU searches
      }

      console.log('🔄 Filtering products...');
      console.log('📊 Total products before filter:', items.length);
      console.log('🔑 Subscribed IDs count:', subscribedProductIds.size);
      console.log('🔍 Sample subscribed IDs:', Array.from(subscribedProductIds).slice(0, 5));

      const filtered = items.filter(product => {
        // For database products: check subscribe property
        if (product.subscribe === true) {
          console.log(`❌ Filtering DB product (subscribe=true):`, {
            title: product.title?.substring(0, 50),
            sku: product.sku,
            offerId: product.offerId,
            _id: product._id
          });
          return false; // Filter out subscribed database products
        }

        // For Alibaba products: check if offerId/sku is in subscribedProductIds
        if (subscribedProductIds.size > 0) {
          const productOfferId = String(product.offerId || product.sku || product._id || '');
          if (productOfferId && subscribedProductIds.has(productOfferId)) {
            console.log(`❌ Filtering Alibaba product (matched offerId):`, {
              title: product.title?.substring(0, 50),
              offerId: product.offerId,
              sku: product.sku,
              _id: product._id,
              matchedId: productOfferId
            });
            return false; // Filter out subscribed Alibaba products
          }
        }

        return true; // Keep the product
      });

      console.log('✅ Products after filter:', filtered.length);
      console.log('🗑️ Filtered out:', items.length - filtered.length, 'products');

      return filtered;
    } catch (error) {
      console.error('❌ Error in filterSubscribedProducts:', error);
      return Array.isArray(items) ? items : [];
    }
  }, [subscribedProductIds, location.search]);

  // Re-apply filter when subscribed IDs are loaded (after products are already displayed)
  // Also backfill from next pages if too many products are filtered out
  React.useEffect(() => {
    const backfillProducts = async () => {
      if (subscribedProductIds.size > 0 && stableProducts.length > 0) {
        console.log('🔄 Subscribed IDs just loaded, re-filtering stable products...');
        const refiltered = filterSubscribedProducts(stableProducts);

        if (refiltered.length < stableProducts.length) {
          console.log(`♻️ Re-filtered: ${stableProducts.length} -> ${refiltered.length} products`);

          const MIN_PRODUCTS_THRESHOLD = 4; // Minimum products we want to show
          const PAGE_SIZE = TOTAL_PRODUCTS;

          // If we have enough products after filtering, just update and return
          if (refiltered.length >= MIN_PRODUCTS_THRESHOLD) {
            setProducts(refiltered);
            setStableProducts(refiltered);
            return;
          }

          // Need to backfill - fetch more products from next pages
          console.log(`📥 Products below threshold (${refiltered.length} < ${MIN_PRODUCTS_THRESHOLD}), starting backfill...`);

          // Prevent concurrent backfills
          if (isBackfillingRef.current) {
            console.log('⏸️ Backfill already in progress, skipping');
            return;
          }

          isBackfillingRef.current = true;
          const params = new URLSearchParams(location.search);
          const searchKeyword = params.get('search');
          const currentPage = parseInt(params.get('page')) || 1;

          // Only backfill for keyword searches (not category browsing)
          if (!searchKeyword) {
            console.log('⏸️ No keyword search, skipping backfill');
            setProducts(refiltered);
            setStableProducts(refiltered);
            isBackfillingRef.current = false;
            return;
          }

          let accumulated = [...refiltered];
          let nextPage = currentPage + 1;
          const maxPagesToFetch = 5; // Don't fetch more than 5 additional pages
          let pagesFetched = 0;

          try {
            setIsLoading(true);

            while (accumulated.length < PAGE_SIZE && pagesFetched < maxPagesToFetch && nextPage <= pages.totalPages) {
              console.log(`📄 Backfilling from page ${nextPage}...`);

              try {
                const mongoResult = await SearchProductStorageService.getProductsByKeyword(searchKeyword, { page: nextPage, limit: PAGE_SIZE });

                if (mongoResult?.status?.code === 200 && mongoResult?.data?.products?.length > 0) {
                  const mongoProducts = mongoResult.data.products.map(product => ({
                    ...product,
                    _id: product._id,
                    sku: String(product.sku || product.offerId || ''),
                    title: product.title || '',
                    imgUrl: Array.isArray(product.imgUrl) ? product.imgUrl : [product.imgUrl].filter(Boolean),
                    image: product.image || product.imgUrl?.[0] || '',
                    moq: Number(product.moq || 1),
                    sp: Number(product.sp || 0),
                    finalPrice: Number(product.sp || 0),
                    location: product.location || 'global',
                    handling_days: Number(product.handling_days || 15),
                    month_sold: Number(product.monthSoldOut || 0),
                    rating: product.rating || 4.0,
                    variations: product.variations || [],
                    mainVideo: product.mainVideo || null,
                    isFromDB: true,
                  }));

                  // Filter the backfilled products
                  const filteredBackfill = filterSubscribedProducts(mongoProducts);
                  console.log(`📦 Page ${nextPage}: ${mongoProducts.length} products, ${filteredBackfill.length} after filter`);

                  // Add unique products only (avoid duplicates)
                  const existingIds = new Set(accumulated.map(p => p.sku || p.offerId || p._id));
                  const uniqueProducts = filteredBackfill.filter(p => {
                    const productId = p.sku || p.offerId || p._id;
                    return !existingIds.has(productId);
                  });

                  accumulated = [...accumulated, ...uniqueProducts];
                  console.log(`📊 Accumulated: ${accumulated.length} products`);
                } else {
                  console.log(`📄 Page ${nextPage}: No more products found`);
                  break;
                }
              } catch (fetchError) {
                console.error(`❌ Error fetching page ${nextPage}:`, fetchError);
                break;
              }

              nextPage++;
              pagesFetched++;
            }

            console.log(`✅ Backfill complete: ${accumulated.length} products from ${pagesFetched + 1} pages`);

            // Take only PAGE_SIZE products for display
            const finalProducts = accumulated.slice(0, PAGE_SIZE);
            setProducts(finalProducts);
            setStableProducts(finalProducts);

          } catch (error) {
            console.error('❌ Error during backfill:', error);
            // Fall back to whatever we filtered
            setProducts(refiltered);
            setStableProducts(refiltered);
          } finally {
            setIsLoading(false);
            isBackfillingRef.current = false;
          }
        }
      }
    };

    backfillProducts();
  }, [subscribedProductIds.size]); // Only trigger when size changes (IDs loaded)

  const applySort = items => {
    try {
      const params = new URLSearchParams(location.search);
      const sortBy = params.get('sortBy') || 'recommended';
      const arr = Array.isArray(items) ? [...items] : [];
      if (sortBy === 'lowToHigh') {
        arr.sort((a, b) => Number(a?.sp || a?.consign_price || 0) - Number(b?.sp || b?.consign_price || 0));
      } else if (sortBy === 'highToLow') {
        arr.sort((a, b) => Number(b?.sp || b?.consign_price || 0) - Number(a?.sp || a?.consign_price || 0));
      } else {
        // recommended: keep arrival order; no-op
      }
      return arr;
    } catch {
      return Array.isArray(items) ? items : [];
    }
  };

  const uploadRetryRef = React.useRef(false);
  const initialAliRetryRef = React.useRef(false);
  const searchTimeoutRef = React.useRef(null);
  const imageUploadRef = React.useRef(false); // Track image upload state

  // Helper to wait for a sessionStorage value (for image upload flow)
  const waitForSessionValue = async (key, tries = 8, delayMs = 150) => {
    for (let i = 0; i < tries; i++) {
      const v = sessionStorage.getItem(key);
      if (v) return v;
      await new Promise(res => setTimeout(res, delayMs));
    }
    return null;
  };

  useEffect(() => {
    // Skip if showing subscribed products - pagination is handled separately
    if (showSubscribedProducts) {
      return;
    }

    // Clear any existing timeout to prevent multiple rapid calls
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    // Skip if already searching to prevent loops
    if (isSearching.current) {
      return;
    }

    // Check if there's an active request
    const currentRequestId = sessionStorage.getItem('current_request_id');
    if (currentRequestId) {
      const requestTime = parseInt(currentRequestId);
      const now = Date.now();
      if (now - requestTime < 30000) {
        // 30 seconds
        console.log('Active request detected, skipping useEffect');
        return;
      } else {
        // Remove stale request
        sessionStorage.removeItem('current_request_id');
      }
    }

    // Additional check for global upload lock
    const globalUploadLock = sessionStorage.getItem('global_image_upload_lock');
    if (globalUploadLock) {
      const lockTimestamp = parseInt(globalUploadLock);
      const now = Date.now();
      if (now - lockTimestamp < 30000) {
        console.log('Global upload lock active, skipping useEffect');
        return;
      } else {
        // Remove stale lock
        sessionStorage.removeItem('global_image_upload_lock');
      }
    }

    // Debounce the search to prevent multiple rapid API calls

    // Reset state immediately on location/search change to prevent "No products found" flash
    // during the debounce period. Component reuse means isLoading might be false from previous page.
    const params = new URLSearchParams(location.search);
    const ali = params.get('ali');
    const cached = sessionStorage.getItem('alibaba_image_results');
    const hasCache = ali === 'upload' && cached;

    setHasSearched(false); // Always hide "No products found" during transition

    if (!hasCache) {
      setIsLoading(true); // Show loader/skeletons during debounce
    }

    searchTimeoutRef.current = setTimeout(() => {
      // Double-check we're not already searching
      if (isSearching.current) {
        return;
      }
      const params = new URLSearchParams(location.search);
      const ali = params.get('ali');
      const currentPage = parseInt(params.get('page')) || 1;
      let searchParam = params.get('search'); // mutable for slug fallback
      const categoryParam = params.get('category');
      logger.debug('Products useEffect triggered', {
        ali,
        location: location.search,
        currentPage,
      });

      // When sliders are moving (apply=0), keep loader visible and skip fetching
      const applyFlag = params.get('apply');
      if (applyFlag === '0') {
        setIsLoading(true);
        return;
      }

      // Ensure category=global is present whenever ali is set
      if (ali && String(categoryParam || '').toLowerCase() !== 'global') {
        const next = new URLSearchParams(location.search);
        next.set('category', 'global');
        navigate(`${location.pathname}?${next.toString()}`, { replace: true });
        return; // wait for URL to update
      }

      // inject slug-based keyword if no explicit ?search param
      if (!searchParam && location.pathname.startsWith('/category/')) {
        const slugParts = location.pathname.split('/').filter(Boolean).slice(1);
        if (slugParts.length) {
          // Convert category and subCategory slugs to search keyword
          searchParam = slugParts.join(' ');
          // keep queryParam synced
          setQueryParam(`?search=${encodeURIComponent(searchParam)}`);
        }
      }

      // For image upload, first check if we have cached results to show immediately
      if (ali === 'upload') {
        // Prevent multiple image upload calls
        if (imageUploadRef.current) {
          console.log('Image upload already in progress, skipping duplicate call');
          return;
        }

        // Check if we have a global upload lock to prevent multiple simultaneous uploads
        const globalUploadLock = sessionStorage.getItem('global_image_upload_lock');
        if (globalUploadLock) {
          const lockTimestamp = parseInt(globalUploadLock);
          const now = Date.now();
          // If lock is recent (within 30 seconds), skip
          if (now - lockTimestamp < 30000) {
            console.log('Global image upload in progress, skipping duplicate call');
            return;
          } else {
            // Remove stale lock
            sessionStorage.removeItem('global_image_upload_lock');
          }
        }

        const cached = sessionStorage.getItem('alibaba_image_results');
        if (cached) {
          try {
            const allMapped = filterSubscribedProducts(JSON.parse(cached));
            if (Array.isArray(allMapped) && allMapped.length > 0) {
              setFullResultSet(allMapped);
              setStableFullResultSet(allMapped);
              const itemsPerPage = TOTAL_PRODUCTS;
              const startIndex = (currentPage - 1) * itemsPerPage;
              const endIndex = startIndex + itemsPerPage;
              const mapped = allMapped.slice(startIndex, endIndex);
              setProducts(mapped);
              setStableProducts(mapped);
              const totalItems = allMapped.length;
              const totalPages = Math.ceil(totalItems / itemsPerPage);
              setPages(prev => ({
                ...prev,
                currentPage: currentPage,
                totalPages: totalPages,
                totalItems: totalItems,
              }));
              console.log('Showing cached results while processing upload:', {
                total: allMapped.length,
                currentPage: mapped.length,
              });
              // Do NOT show the loader overlay if cached results are already visible
              // Proceed to refresh in background without toggling isLoading
              setIsLoading(false);
              setHasSearched(true);
              handleProducts('', { silent: true });
              return;
            }
          } catch (e) {
            console.log('Error parsing cached results:', e);
          }
        }
        logger.debug('Calling handleProducts for image upload');
        // For first-time image upload search, don't show the top loader overlay.
        // Fetch in background and render when ready.
        handleProducts('', { silent: true });
      } else if (tagKeyword && location.pathname.startsWith('/tag/')) {
        // Prevent duplicate tag search calls
        const currentTagUrl = `${location.pathname}${location.search}`;
        if (isSearching.current || lastTagSearchRef.current === currentTagUrl) {
          logger.debug('Tag search already in progress or duplicate URL, skipping');
          return;
        }
        lastTagSearchRef.current = currentTagUrl;
        logger.debug('Calling handleProducts for tag search');
        handleProducts('');
      } else if (searchParam) {
        logger.debug('Calling handleProducts for keyword search');
        // if queryParam hasn’t been updated yet (e.g. slug route), build query here
        const qp = queryParam || `?search=${encodeURIComponent(searchParam)}`;
        handleProducts(qp);
      } else if (ali || (categoryParam && String(categoryParam).toLowerCase() === 'global')) {
        logger.debug('Calling handleProducts for Alibaba search');
        // If the user selected upload method but ali isn't set yet, detect via session and fetch silently once
        try {
          const b64 = sessionStorage.getItem('alibaba_image_upload_b64');
          const timestamp = sessionStorage.getItem('alibaba_image_timestamp');
          if (!ali && b64 && !initialAliRetryRef.current) {
            initialAliRetryRef.current = true; // prevent repeated silent calls
            handleProducts('', { silent: true });
            return;
          }
        } catch { }
        handleProducts('');
      } else if (categoryParam && String(categoryParam).toLowerCase() !== 'global') {
        logger.debug('Calling handleProducts for regular category search');
        handleProducts(queryParam || '');
      } else if (!categoryParam || String(categoryParam).toLowerCase() !== 'global') {
        logger.debug('Calling handleProducts for default search');
        handleProducts('?featured=true&limit=24');
      } else {
        logger.debug('Alibaba category selected with no search, skipping regular product API calls');
      }
      window.scrollTo(0, 0);
    }, 100); // 100ms debounce delay

    // Cleanup function to clear timeout on unmount
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
      // Reset search flag on unmount
      isSearching.current = false;
      imageUploadRef.current = false;
    };
  }, [location.search, location.pathname, tagKeyword, queryParam]);

  // Reset filter buttons when search keyword changes (but preserve dropship filter across pages)
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const searchParam = params.get('search');
    const currentPage = parseInt(params.get('page')) || 1;

    // Get previous search keyword from sessionStorage to detect keyword changes
    const prevSearchKeyword = sessionStorage.getItem('last_search_keyword');

    // Only reset filter states when search keyword actually changes (not just page number)
    if (prevSearchKeyword !== searchParam) {
      setShowAmazonFriendly(false);
      setShowSubscribedProducts(false);
      setAllSubscribedProducts([]); // Clear subscribed products when keyword changes
      setSubscribedProductsPage(1); // Reset page
      setShowDropshipProducts(false);
      setSkeletonCount(0);
      // Clear dropship filter state when keyword changes
      sessionStorage.removeItem('dropship_filter_active');
      sessionStorage.removeItem('dropship_filter_keyword');
      // Store current search keyword
      if (searchParam) {
        sessionStorage.setItem('last_search_keyword', searchParam);
      } else {
        sessionStorage.removeItem('last_search_keyword');
      }
    } else {
      // If search keyword is the same (just page changed), restore dropship filter state
      const savedDropshipFilter = sessionStorage.getItem('dropship_filter_active');
      const savedDropshipKeyword = sessionStorage.getItem('dropship_filter_keyword');
      if (savedDropshipFilter === 'true' && savedDropshipKeyword === searchParam) {
        // Keep the filter active (the actual filtering happens when products load)
        setShowDropshipProducts(true);
        console.log('🔄 Preserving dropship filter across page navigation');
      }
    }
  }, [location.search]); // Watch URL search params change (includes search keyword and page)

  // Retry once if Alibaba search produced no items on the first attempt (cold start)
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const ali = params.get('ali');
    const searchParam = params.get('search');
    const isAli = !!(ali || (params.get('category') && String(params.get('category')).toLowerCase() === 'global'));

    // Skip retry for keyword searches (already handled in main useEffect)
    if (searchParam) return;

    // Keep image upload flow silent; no retry overlay
    if (ali === 'upload') return;

    // Check for active request
    const currentRequestId = sessionStorage.getItem('current_request_id');
    if (currentRequestId) {
      const requestTime = parseInt(currentRequestId);
      const now = Date.now();
      if (now - requestTime < 30000) {
        return;
      }
    }

    // Check for global upload lock
    const globalUploadLock = sessionStorage.getItem('global_image_upload_lock');
    if (globalUploadLock) {
      const lockTimestamp = parseInt(globalUploadLock);
      const now = Date.now();
      if (now - lockTimestamp < 30000) {
        return;
      }
    }

    if (isAli && !isLoading && products.length === 0 && !initialAliRetryRef.current && !isSearching.current) {
      initialAliRetryRef.current = true;
      handleProducts('');
    }
  }, [location.search, isLoading, products.length]);

  useEffect(() => {
    // console.log('📄 Page changed to:', page);
    // console.log('🔗 Current URL:', location.search);

    // Skip if showing subscribed products - pagination is handled separately
    if (showSubscribedProducts) {
      return;
    }

    if (page === 1) {
      handleRemovePageParam();
    } else if (page > 1) {
      handleAddPageParam();
    }

    // This prevents duplicate API calls for tag routes
    if (tagKeyword && location.pathname.startsWith('/tag/')) {
      // Tag searches are handled by the location.search useEffect, skip here to avoid duplicates
      return;
    }

    // For initial load (page null or 1), the main useEffect (location.search) already calls handleProducts.
    // Only trigger fetch from this effect when page > 1 to avoid duplicate combined-search API calls.
    if (page === null || page === 1) {
      return;
    }

    // Only trigger fetch if page actually changed and we're not already loading or searching
    // This prevents duplicate API calls when the page changes due to URL updates
    if (!isLoading && !isSearching.current && !imageUploadRef.current) {
      const params = new URLSearchParams(location.search);
      const ali = params.get('ali');
      const isSimilarSearch = params.get('similarSearch') === 'true';

      // Check for active request
      const currentRequestId = sessionStorage.getItem('current_request_id');
      if (currentRequestId) {
        const requestTime = parseInt(currentRequestId);
        const now = Date.now();
        if (now - requestTime < 30000) {
          console.log('⏸️ Active request detected during page change, skipping');
          return;
        }
      }

      // Skip upload lock check for similar searches to allow pagination
      if (!isSimilarSearch) {
        const globalUploadLock = sessionStorage.getItem('global_image_upload_lock');
        if (globalUploadLock) {
          const lockTimestamp = parseInt(globalUploadLock);
          const now = Date.now();
          if (now - lockTimestamp < 30000) {
            console.log('⏸️ Global upload lock active during page change, skipping');
            return;
          }
        }
      }

      try {
        const categoryParam = params.get('category');

        // Handle different search scenarios
        if (ali === 'ready') {
          // New image search API with pagination
          console.log('🚀 Triggering handleProducts for ali=ready (new image search API)');
          setTimeout(() => {
            handleProducts(location.search || '');
          }, 0);
        } else if (ali === 'upload') {
          // Don't use silent mode for similar searches to show loader
          // console.log('🚀 Triggering handleProducts for ali=upload, similarSearch:', isSimilarSearch);
          setTimeout(() => {
            handleProducts(location.search || '', { silent: !isSimilarSearch });
          }, 0);
        } else if (params.get('search')) {
          // Allow keyword searches even when Alibaba category is selected
          console.log('🚀 Triggering handleProducts for keyword search');
          setTimeout(() => {
            handleProducts(location.search || '');
          }, 0);
        } else if (!categoryParam || String(categoryParam).toLowerCase() !== 'global') {
          console.log('🚀 Triggering handleProducts for regular category');
          setTimeout(() => {
            handleProducts(location.search || '');
          }, 0);
        } else {
          console.log('⏸️ Alibaba category selected with no search during page change, skipping regular API calls');
        }
      } catch (err) {
        console.error('❌ Error in page change handler:', err);
      }
    }
  }, [page]); // Removed isLoading to prevent circular dependency

  // DEDICATED: Handle similar search pagination when page state changes
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const isSimilarSearch = params.get('similarSearch') === 'true';
    const ali = params.get('ali');
    const similarSearchImageUrl = sessionStorage.getItem('similarSearchImageUrl');

    // Only handle similar search pagination
    if (ali === 'upload' && isSimilarSearch && similarSearchImageUrl) {
      // Get page from URL if page state is null
      const urlPage = parseInt(params.get('page')) || 1;
      const effectivePage = page || urlPage;

      // Skip page 1 on initial load if we already have products
      if (effectivePage === 1 && products.length > 0) {
        console.log('⏭️ Page 1 already has products, skipping');
        return;
      }

      // Force API call for this page
      // console.log('🔥 FORCING API CALL for page:', effectivePage);

      // Reset all blocking flags
      imageUploadRef.current = false;
      sessionStorage.removeItem('global_image_upload_lock');
      sessionStorage.removeItem('global_search_lock');

      // Wait a tiny bit then call API
      setTimeout(() => {
        if (!isSearching.current) {
          // console.log('✅ EXECUTING handleProducts for page:', effectivePage);
          handleProducts(location.search || '');
        } else {
          console.log('⚠️ isSearching is true, waiting...');
        }
      }, 100);
    }
  }, [page, location.search]); // Watch both page state and URL

  // Old pagination handler removed - now always uses API for ali=ready
  // The handleProducts function handles all pagination via API calls

  const handleRemovePageParam = () => {
    const searchParams = new URLSearchParams(location.search);
    searchParams.delete('page');
    navigate(`${location.pathname}?${searchParams.toString()}`);
  };

  const handleAddPageParam = () => {
    const currentQueries = queryString.parse(location.search);
    // console.log('🔧 handleAddPageParam - Current queries:', currentQueries);
    // console.log('🔧 handleAddPageParam - location.search:', location.search);
    // console.log('🔧 handleAddPageParam - Adding page:', page);

    // Build new query params, preserving ALL existing parameters
    const newQueryParams = {
      ...currentQueries,
      page: page,
    };

    // Special handling for similar search - ensure these params are preserved
    const isSimilarSearch = currentQueries.similarSearch === 'true' || sessionStorage.getItem('similarSearchImageUrl');

    if (isSimilarSearch && !currentQueries.ali) {
      console.log('⚠️ Missing ali parameter, restoring from context');
      newQueryParams.ali = 'upload';
    }

    if (isSimilarSearch && !currentQueries.similarSearch) {
      console.log('⚠️ Missing similarSearch parameter, restoring');
      newQueryParams.similarSearch = 'true';
    }

    // console.log('🔧 handleAddPageParam - New query params:', newQueryParams);
    const newQueryString = queryString.stringify(newQueryParams);
    // console.log('🔧 handleAddPageParam - New URL:', `${location.pathname}?${newQueryString}`);

    navigate(`${location.pathname}?${newQueryString}`);
  };

  const handleCategoryContent = () => {
    try {
      if (!contentId || contentId === '' || contentId === undefined || contentId.toLowerCase() === 'global') {
        setContent();
        return;
      }
      ProductsApiService.getCategoryContent(contentId)
        .then(res => {
          if (res.status.code === 200) {
            setContent(res.data);
          } else {
            setContent();
          }
        })
        .catch(err => {
          console.error('Category content error:', err);
          setContent();
          message.showToast(err?.message || 'Server error. Try again later', ToastTypes.ERROR);
        });
    } catch (error) {
      console.error('Category content useEffect error:', error);
      setContent();
    }
  };

  useEffect(() => {
    try {
      if (contentId && contentId !== '' && contentId !== undefined) {
        handleCategoryContent();
      } else {
        setContent();
      }
    } catch (error) {
      console.error('Category content useEffect error:', error);
      setContent();
    }
  }, [contentId]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const categoryParam = params.get('category');
    if (categoryParam) {
      setContentId(categoryParam);
    } else {
      setContentId('');
    }
  }, [location.search]);

  // Initialize page state from URL
  useEffect(() => {
    let pageNo = parseInt(searchParams.get('page'));
    if (pageNo && pageNo !== 1) {
      setPage(pageNo);
    } else {
      setPage(1);
    }
  }, [searchParams.get('page')]);

  // Derive isAlibabaSearch from URL
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const ali = params.get('ali');
    const categoryParam = params.get('category');
    const isAli = !!(ali || (categoryParam && String(categoryParam).toLowerCase() === 'global'));
    setIsAlibabaSearch(isAli);
  }, [location.search]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      // Clear any pending timeouts
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
      // Reset search state
      isSearching.current = false;
      imageUploadRef.current = false;
      setIsLoading(false);
      // Clean up global upload lock
      sessionStorage.removeItem('global_image_upload_lock');
    };
  }, []);

  // Cleanup stale locks on component mount
  useEffect(() => {
    // Clean up any stale locks that are older than 5 minutes
    const globalUploadLock = sessionStorage.getItem('global_image_upload_lock');
    if (globalUploadLock) {
      const lockTimestamp = parseInt(globalUploadLock);
      const now = Date.now();
      if (now - lockTimestamp > 300000) {
        // 5 minutes
        sessionStorage.removeItem('global_image_upload_lock');
        console.log('Cleaned up stale global upload lock');
      }
    }

    // Clean up any stale fetching locks
    const keys = Object.keys(sessionStorage);
    keys.forEach(key => {
      if (key.startsWith('ali_fetching_')) {
        const timestamp = sessionStorage.getItem(key);
        if (timestamp) {
          const lockTime = parseInt(timestamp);
          const now = Date.now();
          if (now - lockTime > 60000) {
            // 1 minute
            sessionStorage.removeItem(key);
            // console.log('Cleaned up stale fetching lock:', key);
          }
        }
      }
    });
  }, []);

  // Generate dynamic SEO based on page context
  const generatePageSEO = () => {
    const params = new URLSearchParams(location.search);
    const searchKeyword = params.get('search');
    const categoryParam = params.get('category');

    if (searchKeyword) {
      // Search results page
      const resultCount = pages.totalItems || products.length || 0;
      return generateSearchSEO(searchKeyword, resultCount);
    } else if (categoryParam) {
      // Category page
      return generateCategorySEO(categoryParam);
    } else {
      // Default products page
      return pageSEO.products;
    }
  };

  const seoData = generatePageSEO();

  // Critical LCP image preloading
  useEffect(() => {
    const allProducts = products?.length > 0 ? products : stableProducts;
    if (allProducts?.length > 0) {
      // Immediately preload first 3 images with highest priority
      allProducts.slice(0, 3).forEach((product, index) => {
        const imgUrl = product?.imgUrl?.[0];
        if (imgUrl && typeof imgUrl === 'string') {
          const httpsUrl = imgUrl.replace(/^http:/, 'https:');

          // Create preload link with highest priority
          const link = document.createElement('link');
          link.rel = 'preload';
          link.as = 'image';
          link.href = httpsUrl;
          link.fetchPriority = 'high';
          document.head.appendChild(link);

          // Also create Image object for immediate loading
          const img = new Image();
          img.loading = 'eager';
          img.fetchPriority = 'high';
          img.src = httpsUrl;
        }
      });
    }
  }, [products, stableProducts]);

  // Preload images from cached data immediately on mount
  useEffect(() => {
    const cachedResults = sessionStorage.getItem('alibaba_image_results');
    if (cachedResults && !products?.length) {
      try {
        const cached = JSON.parse(cachedResults);
        if (cached?.length > 0) {
          const firstImg = cached[0]?.imgUrl?.[0];
          if (firstImg && typeof firstImg === 'string') {
            const link = document.createElement('link');
            link.rel = 'preload';
            link.as = 'image';
            link.href = firstImg.replace(/^http:/, 'https:');
            link.fetchPriority = 'high';
            document.head.appendChild(link);
          }
        }
      } catch (e) { }
    }
  }, []);

  return (
    <>
      <SEO {...seoData} />
      <div className="collection_page">
        <div className="collection_header">
          <BreadCrumbs />
        </div>

        <div className="product_listing_row">
          {/* Left sidebar - Search Navigator */}
          {/* SearchNavigator temporarily disabled
          {isAlibabaSearch && (
            <div className="product_listing_filteration_col search_nav_visible">
              <SearchNavigator currentKeyword={currentKeyword} products={products.length ? products : stableProducts} />
            </div>
          )}
          */}
          <div className="product_listing_products_col">
            {/* Mobile-only Search Navigator (horizontal scrollable) */}
            {/* SearchNavigator mobile temporarily disabled
            {isAlibabaSearch && (
              <div className="search_nav_mobile_wrapper">
                <SearchNavigator currentKeyword={currentKeyword} products={products.length ? products : stableProducts} />
              </div>
            )}
            */}
            <div className="collection_header">
              <div className="sorting-and-filters">
                <div className="filter-buttons-left">
                  <button
                    className={`filter-btn filter-btn-secondary ${showAmazonFriendly ? 'active' : ''}`}
                    onClick={handleAmazonFriendlyClick}
                    title={showAmazonFriendly ? 'Show All Products' : 'Amazon Friendly'}
                  >
                    {showAmazonFriendly ? 'Show All Products' : 'Amazon Friendly'}
                  </button>
                  <button
                    className={`filter-btn filter-btn-secondary ${showSubscribedProducts ? 'active' : ''}`}
                    onClick={handleSubscribeProductClick}
                    title={showSubscribedProducts ? 'Show All Products' : 'Subscribe Product'}
                  >
                    {showSubscribedProducts ? 'Show All Products' : 'Subscribe Product'}
                  </button>
                  <button
                    className={`filter-btn filter-btn-secondary ${showDropshipProducts ? 'active' : ''}`}
                    onClick={handleDropshipProductClick}
                    title={showDropshipProducts ? 'Show All Products' : '1-Piece Product'}
                  >
                    {showDropshipProducts ? 'Show All Products' : '1-Piece Product'}
                  </button>
                  {isAlibabaSearch && isAuthenticatedSeller() && getSelectableProductsCount() > 0 && (
                    <button
                      // className={`filter-btn filter-btn-secondary ${getSelectedProductsOnPageCount() === getSelectableProductsCount() ? 'active' : ''}`}
                      className="filter-btn filter-btn-secondary"
                      onClick={handleSelectAll}
                      // disabled={getSelectedProductsOnPageCount() === getSelectableProductsCount()}
                      // title="Subscribe to all products on this page"
                      disabled={subscriptionLoading}
                      title="Subscribe all products on this page"
                    >
                      Subscribe All
                    </button>
                  )}
                </div>
                <div className="sorting-container-right">
                  <Sorting />
                </div>
                <div className="simple-filters-section">
                  <div className="filter-option">
                    <label>Price Range (₹)</label>
                    <div className="price-range-inputs">
                      <input
                        type="number"
                        placeholder="Min"
                        value={priceRange[0]}
                        onChange={(e) => setPriceRange([Number(e.target.value), priceRange[1]])}
                      />
                      <span>-</span>
                      <input
                        type="number"
                        placeholder="Max"
                        value={priceRange[1]}
                        onChange={(e) => setPriceRange([priceRange[0], Number(e.target.value)])}
                      />
                    </div>
                  </div>

                  <div className="filter-option">
                    <label>Minimum Rating</label>
                    <select
                      value={ratingFilter}
                      onChange={(e) => setRatingFilter(Number(e.target.value))}
                    >
                      <option value={0}>Any Rating</option>
                      <option value={4}>4+ Stars</option>
                      <option value={3}>3+ Stars</option>
                      <option value={2}>2+ Stars</option>
                      <option value={1}>1+ Star</option>
                    </select>
                  </div>

                  <div className="filter-option">
                    <label>1-Piece Only</label>
                    <div className="checkbox-filter">
                      <input
                        type="checkbox"
                        checked={dropshippingFilter}
                        onChange={(e) => setDropshippingFilter(e.target.checked)}
                      />
                      <span>Show 1-piece products only</span>
                    </div>
                  </div>

                  <div className="filter-option">
                    <label>Advanced Filters</label>
                    <button
                      className="advance-filter-btn"
                      onClick={handleAdvanceFilterClick}
                    >
                      Advanced
                    </button>
                  </div>

                  <div className="filter-actions">
                    <button
                      className="apply-filters-btn"
                      onClick={handleApplyFilters}
                    >
                      Apply
                    </button>
                    <button
                      className="reset-filters-btn"
                      onClick={handleResetFilters}
                    >
                      Reset
                    </button>
                  </div>
                </div>
              </div>
            </div>


            <div className="collection_product_row">
              {/* SKELETON: Show full skeleton when loading new page (skeletonCount === 0) */}
              {isLoading && skeletonCount === 0 &&
                Array.from({ length: TOTAL_PRODUCTS }).map((_, idx) => (
                  <div key={`skeleton-${idx}`} className="product_card_box skeleton_card">
                    <div className="skeleton_img" />
                    <div className="skeleton_text skeleton_text-lg" />
                    <div className="skeleton_text skeleton_text-sm" />
                  </div>
                ))
              }
              {/* Only show products when NOT loading with full skeleton (regular page load) */}
              {!(isLoading && skeletonCount === 0) && (() => {
                // Limit products for non-logged-in users
                const allProducts = (products && products.length ? products : stableProducts) || [];
                const displayProducts = isLoggedIn() ? allProducts : allProducts.slice(0, TOTAL_PRODUCTS);

                return displayProducts.map((product, index) => {
                  const isSelectable = isAlibabaSearch && isAuthenticatedSeller();
                  const isSelected = isProductSelected(product);
                  // Determine if this is a featured category page
                  const isFromFeaturedCategory = location.pathname.includes('featured') ||
                    location.search.includes('fcId=') ||
                    location.search.includes('category=featured');
                  return (
                    <ProductCard
                      key={product._id || product.sku || product.offerId}
                      product={product}
                      index={index}
                      isSelectable={isSelectable}
                      isSelected={isSelected}
                      onSelectionChange={handleProductSelection}
                      keyword={currentKeyword}
                      priority={index < 3 ? 'high' : 'low'}
                      loading={index < 3 ? 'eager' : 'lazy'}
                      isFromFeaturedCategory={isFromFeaturedCategory}
                      onSendInquiry={setInquiryProduct}
                    />
                  );
                });
              })()}
              {/* Show skeleton cards for remaining products while fetching more (dropship filter) */}
              {skeletonCount > 0 &&
                Array.from({ length: skeletonCount }).map((_, idx) => (
                  <div key={`loading-skeleton-${idx}`} className="product_card_box skeleton_card">
                    <div className="skeleton_img" />
                    <div className="skeleton_text skeleton_text-lg" />
                    <div className="skeleton_text skeleton_text-sm" />
                  </div>
                ))
              }
              {/* Show login prompt if not logged in and there are more than 4 products */}
              {!isLoggedIn() && ((products && products.length > 4) || (stableProducts && stableProducts.length > 4)) && (
                <LoginExploreCard totalProducts={products?.length || stableProducts?.length || 0} />
              )}
              {!isLoading && !isCategoryLoading && hasSearched && (!products || products.length === 0) && (!stableProducts || stableProducts.length === 0) ? (
                <div style={{ gridColumn: '1 / -1', width: '100%', textAlign: 'center', padding: '40px 0', color: '#6b7280', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                  <h3 style={{ margin: 0, color: '#111827', textAlign: 'center', width: '100%' }}>No products found</h3>
                  <p style={{ marginTop: '8px', textAlign: 'center', width: '100%' }}>Try adjusting your filters or search keyword.</p>
                </div>
              ) : null}
            </div>
            {/* Loader removed completely to avoid blank space */}
            {/* Keyword search loader (regular and Global/Alibaba) */}
            {isLoading && (
              <div
                style={{
                  position: 'fixed',
                  top: '180px',
                  left: '50%',
                  transform: 'translateX(-50%)',
                  backgroundColor: 'rgba(255,255,255,0.95)',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  zIndex: 1000,
                  borderRadius: '12px',
                  padding: '12px 16px',
                  minWidth: '260px',
                  boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
                  border: '1px solid #e5e7eb',
                }}
              >
                <div
                  style={{
                    width: '22px',
                    height: '22px',
                    border: '3px solid #e5e7eb',
                    borderTop: '3px solid #3DD0E5',
                    borderRadius: '50%',
                    animation: 'spin 1s linear infinite',
                  }}
                />
                <div style={{ display: 'flex', flexDirection: 'column' }}>
                  <strong style={{ margin: 0, color: '#004C81', fontSize: '14px' }}>Loading products</strong>
                  <span style={{ margin: 0, color: '#6b7280', fontSize: '12px' }}>Searching… please wait</span>
                </div>
              </div>
            )}
            {pages.totalPages > 1 && (products?.length > 0 || stableProducts?.length > 0) && isLoggedIn() ? (
              <div className="collection_pagination">
                {showSubscribedProducts && allSubscribedProducts.length > 0 && (
                  <div style={{ marginBottom: '10px', textAlign: 'center', fontSize: '14px', color: '#666', fontWeight: '500' }}>
                    {products.length}/{pages.totalItems} products
                  </div>
                )}
                <Pagination
                  pages={pages}
                  page={page}
                  setPage={(newPage) => {
                    setPage(newPage);
                    // Update URL with new page
                    const params = new URLSearchParams(location.search);
                    if (newPage === 1) {
                      params.delete('page');
                    } else {
                      params.set('page', newPage.toString());
                    }
                    navigate(`${location.pathname}?${params.toString()}`, { replace: true });
                    // Scroll to top when page changes
                    window.scrollTo({ top: 0, behavior: 'smooth' });
                  }}
                />
              </div>
            ) : null}
          </div>
        </div>
        <div className="collection_footer">
          <MobileFilteration setQuery={setQueryParam} setContentId={setContentId} setPage={setPage} />
        </div>
      </div>
      {/* Multi-product subscription components */}
      {/* {isAlibabaSearch && getTotalSelectedCount() > 0 && (
        <StickySelectionBar
          selectedProductsByKeyword={selectedProductsByKeyword}
          onSubscribe={handleOpenSubscriptionModal}
          onClear={handleClearSelections}
          isLoading={subscriptionLoading}
        />
      )} */}

      {/* Subscription Modal */}
      <SubscriptionModal
        show={showSubscriptionModal}
        onHide={() => setShowSubscriptionModal(false)}
        selectedProductsByKeyword={selectedProductsByKeyword}
        onSubscribe={handleMultipleSubscription}
        onProductRemove={handleProductRemoveFromModal}
        isLoading={subscriptionLoading}
      />

      {/* Request for quote modal - when Send Inquiry clicked on list (zero weight/dimension products) */}
      {inquiryProduct && (
        <ContactSupplierSection
          product={inquiryProduct}
          isOpen={!!inquiryProduct}
          setIsOpen={() => setInquiryProduct(null)}
        />
      )}

      {/* Spinner keyframes for loader */}
      <style>{`@keyframes spin { 0% { transform: rotate(0deg);} 100% { transform: rotate(360deg);} }`}</style>
    </>
  );
}
